/**
 * @fileoverview Shared DataGrid component for tenant-web and system-web.
 * @module ui-admin/DataGrid
 *
 * Responsibilities:
 * - Renders paginated, sortable, filterable data grids backed by TanStack Table.
 * - Provides shared row-action menus (three-dot and right-click context menu),
 *   including keyboard-accessible child flyouts clamped to the viewport.
 * - Supports keyboard invocation/focus management for generic row actions, row selection,
 *   multi-select, host-controlled native row drag/drop, column reordering/grouping/visibility,
 *   query builder, and bulk actions.
 * - Exports data to CSV, Excel (ZIP), and PDF.
 *
 * Dependencies:
 * - @tanstack/react-table for table state and rendering.
 * - lucide-react for icons.
 * - fflate for ZIP export, jspdf/jspdf-autotable for PDF export.
 *
 * Architecture:
 * - @reusable Shared across every grid page in tenant-web and system-web.
 *   Presentation + interaction only — never fetches its own data; consumers
 *   pass pre-fetched arrays via the `data` prop from a local-first Dexie service.
 *
 * Notes:
 * - Menu positioning uses `clampMenuPosition()` to keep all popup menus fully
 *   visible within the viewport, with `maxHeight`/`overflowY` as a fallback
 *   for extremely short viewports.
 * - Column visibility/order and contextual overrides persist through gridPersistence.
 *   "Reset to defaults" clears these keys before remount so stale user choices do not
 *   override the page's current factory defaults.
 * - The onStateChange debounce is cancelled on unmount so a pending snapshot
 *   from the previous layout cannot overwrite a just-applied template.
 * - Column Settings Apply writes a complete id→boolean visibility map (hidden
 *   columns are false, the rest true) so a later template Apply can restore a
 *   one-column view after the user has shown every column again.
 * - Saved entries for not-yet-supplied columns are kept; columns retired mid-mount
 *   are pruned, and Reset to defaults drops pending entries.
 * - Expanded sources reuse the same host row-action descriptors and popup menu.
 * - Optional rowDragDrop renders host eligibility/feedback and an explicit outside
 *   drop zone; DataGrid never authorizes or persists domain mutations.
 * - Data rows expose stable focus-recovery attributes without joining the tab sequence.
 * - An active-filter bar under the toolbar lists every mechanism narrowing the
 *   grid (search text, toolbar filter values, per-column filter-row values,
 *   query builder) as removable chips with a Clear-all button — see
 *   filterChips.ts. Column-filter values persist inside GridState.columnFilters
 *   so a filter applied before leaving the page is visible and clearable after
 *   remount; restoring them reopens the filter row. Closing the filter row only
 *   hides it — applied column filters stay active and remain clearable via chips.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { strToU8, zipSync } from 'fflate';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  SortingState,
  ColumnDef as TanStackColumnDef,
  ColumnFiltersState,
} from '@tanstack/react-table';
import {
  Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, X, Download, MoreHorizontal,
  Columns3, SlidersHorizontal, Zap, Plus, Trash2, Loader2,
  CheckSquare, GripVertical, Menu, ArrowUpDown, Layers, Settings2,
  ArrowUp, ArrowDown, Eye, EyeOff, FileText, FileSpreadsheet, FileDown,
} from 'lucide-react';
import type {
  DataGridProps, DataGridColumn, DataGridFilter, DataGridExpandedRowActions, DataGridRowAction, DataGridRowDragDrop, BulkAction,
  QueryCondition, QueryGroup, QueryOperator, GridState, FetchParams,
} from './types';
import { isQueryGroup } from './types';
import {
  applyContextualColumnDefaults,
  gridColumnOrderKey,
  gridColumnVisibilityKey,
  readContextualColumnVisibilityOverrides,
  reconcileColumnLayout,
  retainUnsuppliedColumnLayout,
  resolveTemplateColumnVisibility,
  writeContextualColumnVisibilityOverrides,
} from './gridPersistence';
import {
  buildActiveFilterChips,
  countAppliedGridFilters,
  countQueryConditions,
  summarizeQueryGroup,
  type ActiveFilterChip,
} from './filterChips';
import { clampPopupPosition, getPopupStyle } from '../../utils/viewportPosition';
import type { CursorPoint, PopupPlacement } from '../../utils/viewportPosition';
import { buildCsv, downloadBlob } from '../../utils/csvExport';

// ============================================================
// Sub-components
// ============================================================

function DataGridLoadingState({ columnCount }: { columnCount: number }) {
  return (
    <div className="flex-1">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50">
          {Array.from({ length: Math.min(columnCount, 6) }).map((_, j) => (
            <div key={j} className="h-4 bg-gray-100 rounded-sm animate-pulse"
              style={{ width: `${60 + Math.random() * 100}px` }} />
          ))}
        </div>
      ))}
    </div>
  );
}

function DataGridEmptyState({
  icon: Icon, title, subtitle, cta, hasFilters, onClearFilters,
}: {
  icon?: React.ElementType; title?: string; subtitle?: string;
  cta?: { label: string; onClick: () => void };
  hasFilters: boolean; onClearFilters: () => void;
}) {
  const displayTitle = hasFilters ? 'No results match your filters' : (title || 'No data');
  const displaySubtitle = hasFilters ? 'Try adjusting your search or filter criteria' : (subtitle || '');

  return (
    <div className="flex-1 flex items-center justify-center py-16">
      <div className="text-center">
        {Icon && <Icon className="h-12 w-12 text-gray-300 mx-auto mb-3" />}
        <p className="text-gray-500 font-medium">{displayTitle}</p>
        {displaySubtitle && <p className="text-sm text-gray-400 mt-1">{displaySubtitle}</p>}
        {hasFilters && (
          <button onClick={onClearFilters}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
            <X className="h-4 w-4" /> Clear all filters
          </button>
        )}
        {!hasFilters && cta && (
          <button onClick={cta.onClick}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors">
            {cta.label}
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Menu Viewport Clamping
// ============================================================

/** Viewport-boundary padding (px) kept between a popup menu and the window edge. */
const MENU_VIEWPORT_MARGIN = 8;

/** Per-item height (px) used to estimate popup menu dimensions. */
const MENU_ITEM_HEIGHT = 36;
/** Height of the "Actions" header row present in context menus. */
const MENU_HEADER_HEIGHT = 28;
/** Vertical padding inside each menu container. */
const MENU_PADDING_Y = 8;

function visibleRowActions<T>(
  actions: DataGridRowAction<T>[],
  row: T,
  topLevel = false,
): DataGridRowAction<T>[] {
  return actions.filter((action) => {
    if (topLevel && action.expandedOnly) return false;
    if (action.hidden?.(row)) return false;
    return !action.children || visibleRowActions(action.children, row).length > 0;
  });
}

// ============================================================
// Column Settings Modal (Columns + Sorting + Grouping)
// ============================================================

type ColumnSettingsTab = 'columns' | 'sorting' | 'grouping';

/**
 * @component ManageColumnsModal
 * Purpose: Accessible dialog for editing grid column visibility/order, sorting, and grouping.
 * Props: Reconciled grid state and an apply/close callback pair.
 * Behavior: Keyboard-accessible Show/Hide column controls update draft state until Apply.
 * Data Source: Local (parent-provided column and view state).
 * Constraints: Does not persist directly; parent owns state reconciliation and persistence.
 * Performance: Mounted only while Column Settings is open.
 */
function ManageColumnsModal<T>({

  columns,
  columnVisibility,
  columnOrder,
  sorting: externalSorting,
  groupByColumns: externalGroupBy,
  onApply,
  onClose,
}: {
  columns: DataGridColumn<T>[];
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  sorting: SortingState;
  groupByColumns: string[];
  onApply: (
    visibility: Record<string, boolean>,
    order: string[],
    sorting: SortingState,
    groupBy: string[],
    editedVisibilityIds: string[],
    resetToFactory: boolean,
  ) => void;
  onClose: () => void;
}) {
  const settingsTitleId = React.useId();
  const [activeSettingsTab, setActiveSettingsTab] = useState<ColumnSettingsTab>('columns');

  // --- Column state ---
  const allCols = useMemo(() => {
    const seen = new Set<string>();
    return columns.filter((column) => {
      if (column.id === '_actions' || column.id === '_select' || seen.has(column.id)) return false;
      seen.add(column.id);
      return true;
    });
  }, [columns]);
  const columnSchemaKey = JSON.stringify(allCols.map(({ id, hidden }) => [id, hidden]));
  const [localVis, setLocalVis] = useState<Record<string, boolean>>(() => (
    reconcileColumnLayout(allCols, columnVisibility, columnOrder).visibility
  ));
  const [localOrder, setLocalOrder] = useState<string[]>(() => (
    reconcileColumnLayout(allCols, columnVisibility, columnOrder).order
  ));
  const [editedVisibilityIds, setEditedVisibilityIds] = useState<Set<string>>(new Set());
  const [resetToFactory, setResetToFactory] = useState(false);
  const markVisibilityEdited = (ids: string[]) => {
    setEditedVisibilityIds((current) => new Set([...current, ...ids]));
  };

  useEffect(() => {
    setLocalVis((current) => reconcileColumnLayout(allCols, current, []).visibility);
    setLocalOrder((current) => reconcileColumnLayout(allCols, {}, current).order);
  }, [columnSchemaKey]);

  // --- Sorting state ---
  const [localSorting, setLocalSorting] = useState<SortingState>([...externalSorting]);

  // --- Grouping state ---
  const [localGroupBy, setLocalGroupBy] = useState<string[]>([...externalGroupBy]);

  const visibleIds = localOrder.filter(id => localVis[id] !== false);
  const availableIds = allCols.map(c => c.id).filter(id => localVis[id] === false);

  const colLabel = (id: string) => {
    const col = allCols.find(c => c.id === id);
    return col ? (typeof col.header === 'string' ? col.header : col.id) : id;
  };

  // --- Column helpers ---
  const moveToVisible = (id: string, insertAtIdx?: number) => {
    markVisibilityEdited([id]);
    setLocalVis(prev => ({ ...prev, [id]: true }));
    setLocalOrder(prev => {
      if (prev.includes(id)) {
        // Already in order, just make visible
        if (insertAtIdx !== undefined) {
          const without = prev.filter(x => x !== id);
          const visOnly = without.filter(x => localVis[x] !== false || x === id);
          // Find the actual index in the full array
          let targetFullIdx = 0;
          let visCount = 0;
          for (let i = 0; i < without.length; i++) {
            if (localVis[without[i]] !== false) {
              if (visCount === insertAtIdx) { targetFullIdx = i; break; }
              visCount++;
            }
            targetFullIdx = i + 1;
          }
          without.splice(targetFullIdx, 0, id);
          return without;
        }
        return prev;
      }
      if (insertAtIdx !== undefined) {
        const newArr = [...prev];
        // Find actual position in full order for the visible index
        let targetFullIdx = 0;
        let visCount = 0;
        for (let i = 0; i < newArr.length; i++) {
          if (localVis[newArr[i]] !== false) {
            if (visCount === insertAtIdx) { targetFullIdx = i; break; }
            visCount++;
          }
          targetFullIdx = i + 1;
        }
        newArr.splice(targetFullIdx, 0, id);
        return newArr;
      }
      return [...prev, id];
    });
  };

  const moveToAvailable = (id: string) => {
    markVisibilityEdited([id]);
    setLocalVis(prev => ({ ...prev, [id]: false }));
  };

  const showAll = () => {
    const vis: Record<string, boolean> = {};
    allCols.forEach(c => { vis[c.id] = true; });
    markVisibilityEdited(allCols.map(({ id }) => id));
    setLocalVis(vis);
    setLocalOrder(prev => {
      const missing = allCols.map(c => c.id).filter(id => !prev.includes(id));
      return [...prev, ...missing];
    });
  };

  const resetDefault = () => {
    const vis: Record<string, boolean> = {};
    allCols.forEach(c => { if (c.hidden) vis[c.id] = false; });
    setEditedVisibilityIds(new Set());
    setResetToFactory(true);
    setLocalVis(vis);
    setLocalOrder(allCols.map(c => c.id));
    setLocalSorting([]);
    setLocalGroupBy([]);
  };

  // --- Drag state for columns (visible list + cross-list from available) ---
  const [colDragSource, setColDragSource] = useState<{ type: 'visible' | 'available'; id: string; idx: number } | null>(null);
  const [colDropTarget, setColDropTarget] = useState<number | null>(null);

  const handleVisibleDragStart = (idx: number) => {
    setColDragSource({ type: 'visible', id: visibleIds[idx], idx });
  };

  const handleAvailableDragStart = (id: string) => {
    setColDragSource({ type: 'available', id, idx: -1 });
  };

  const handleVisibleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.stopPropagation(); // prevent container's handler from overwriting colDropTarget
    e.dataTransfer.dropEffect = 'move';
    // Determine if we should insert before or after based on mouse position
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const insertIdx = e.clientY < midY ? idx : idx + 1;
    setColDropTarget(insertIdx);
  };

  const handleVisibleListDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // If dragging over empty area at bottom, target = end
    setColDropTarget(visibleIds.length);
  };

  const handleVisibleDrop = () => {
    if (!colDragSource || colDropTarget === null) {
      setColDragSource(null);
      setColDropTarget(null);
      return;
    }

    if (colDragSource.type === 'available') {
      // Move from available to visible at the drop position
      moveToVisible(colDragSource.id, colDropTarget);
    } else {
      // Reorder within visible
      const fromIdx = colDragSource.idx;
      let toIdx = colDropTarget;
      if (fromIdx === toIdx || fromIdx === toIdx - 1) {
        setColDragSource(null);
        setColDropTarget(null);
        return;
      }
      const newVisible = [...visibleIds];
      const [moved] = newVisible.splice(fromIdx, 1);
      // Adjust target if we removed before it
      if (fromIdx < toIdx) toIdx--;
      newVisible.splice(toIdx, 0, moved);
      const hiddenIds = localOrder.filter(id => localVis[id] === false);
      setLocalOrder([...newVisible, ...hiddenIds]);
    }
    setColDragSource(null);
    setColDropTarget(null);
  };

  const handleDragEnd = () => {
    setColDragSource(null);
    setColDropTarget(null);
    setSortDragIdx(null);
    setSortDropTarget(null);
    setGroupDragIdx(null);
    setGroupDropTarget(null);
  };

  const handleAvailableListDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleAvailableDrop = () => {
    if (!colDragSource || colDragSource.type !== 'visible') {
      setColDragSource(null);
      return;
    }
    // Move from visible to available
    moveToAvailable(colDragSource.id);
    setColDragSource(null);
  };

  // --- Sorting helpers ---
  const addSort = (fieldId: string) => {
    if (localSorting.some(s => s.id === fieldId)) return;
    setLocalSorting(prev => [...prev, { id: fieldId, desc: false }]);
  };

  const removeSort = (idx: number) => {
    setLocalSorting(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleSortDir = (idx: number) => {
    setLocalSorting(prev => prev.map((s, i) => i === idx ? { ...s, desc: !s.desc } : s));
  };

  const [sortDragIdx, setSortDragIdx] = useState<number | null>(null);
  const [sortDropTarget, setSortDropTarget] = useState<number | null>(null);

  const handleSortDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    setSortDropTarget(e.clientY < midY ? idx : idx + 1);
  };

  const handleSortDrop = () => {
    if (sortDragIdx === null || sortDropTarget === null) { setSortDragIdx(null); setSortDropTarget(null); return; }
    let toIdx = sortDropTarget;
    if (sortDragIdx === toIdx || sortDragIdx === toIdx - 1) { setSortDragIdx(null); setSortDropTarget(null); return; }
    const newSorting = [...localSorting];
    const [moved] = newSorting.splice(sortDragIdx, 1);
    if (sortDragIdx < toIdx) toIdx--;
    newSorting.splice(toIdx, 0, moved);
    setLocalSorting(newSorting);
    setSortDragIdx(null);
    setSortDropTarget(null);
  };

  // Available fields for sorting (all columns, even hidden, minus already sorted)
  const sortableFields = allCols.filter(c => c.sortable !== false && !localSorting.some(s => s.id === c.id));

  // --- Grouping helpers ---
  const addGroup = (fieldId: string) => {
    if (localGroupBy.includes(fieldId)) return;
    setLocalGroupBy(prev => [...prev, fieldId]);
  };

  const removeGroup = (idx: number) => {
    setLocalGroupBy(prev => prev.filter((_, i) => i !== idx));
  };

  const [groupDragIdx, setGroupDragIdx] = useState<number | null>(null);
  const [groupDropTarget, setGroupDropTarget] = useState<number | null>(null);

  const handleGroupDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    setGroupDropTarget(e.clientY < midY ? idx : idx + 1);
  };

  const handleGroupDrop = () => {
    if (groupDragIdx === null || groupDropTarget === null) { setGroupDragIdx(null); setGroupDropTarget(null); return; }
    let toIdx = groupDropTarget;
    if (groupDragIdx === toIdx || groupDragIdx === toIdx - 1) { setGroupDragIdx(null); setGroupDropTarget(null); return; }
    const newGroup = [...localGroupBy];
    const [moved] = newGroup.splice(groupDragIdx, 1);
    if (groupDragIdx < toIdx) toIdx--;
    newGroup.splice(toIdx, 0, moved);
    setLocalGroupBy(newGroup);
    setGroupDragIdx(null);
    setGroupDropTarget(null);
  };

  // Available fields for grouping (all columns minus already grouped)
  const groupableFields = allCols.filter(c => !localGroupBy.includes(c.id));

  // --- Apply ---
  const handleDone = () => {
    const reconciled = reconcileColumnLayout(allCols, localVis, localOrder);
    onApply(
      reconciled.visibility,
      reconciled.order,
      localSorting,
      localGroupBy,
      Array.from(editedVisibilityIds),
      resetToFactory,
    );
    onClose();
  };

  // --- Insertion indicator component ---
  const InsertionLine = () => (
    <div className="flex items-center gap-1 h-0 relative z-10 pointer-events-none">
      <div className="w-2.5 h-2.5 rounded-full bg-orange-500 border-2 border-white shadow-xs shrink-0 -ml-1 pointer-events-none" />
      <div className="flex-1 h-0.5 bg-orange-500 rounded-full pointer-events-none" />
    </div>
  );

  const settingsTabs: { id: ColumnSettingsTab; label: string; icon: React.ElementType; badge?: number }[] = [
    { id: 'columns', label: 'Columns', icon: Columns3, badge: visibleIds.length },
    { id: 'sorting', label: 'Sorting', icon: ArrowUpDown, badge: localSorting.length || undefined },
    { id: 'grouping', label: 'Grouping', icon: Layers, badge: localGroupBy.length || undefined },
  ];

  return (
    <div className="fixed inset-0 z-100 flex items-start justify-center pt-12" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={settingsTitleId}
        className="relative bg-white rounded-xl shadow-2xl border border-gray-200 w-[740px] h-[600px] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <Settings2 className="h-5 w-5 text-gray-500" />
            <div>
              <h2 id={settingsTitleId} className="text-base font-semibold text-gray-900">Column Settings</h2>
              <p className="text-xs text-gray-500 mt-0.5">Configure columns, sorting, and grouping</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={showAll} className="text-xs font-medium text-orange-600 hover:text-orange-700">Show All</button>
            <button onClick={resetDefault} className="text-xs font-medium text-orange-600 hover:text-orange-700">Reset</button>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-sm">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-6 gap-0 bg-gray-50/50">
          {settingsTabs.map(tab => (
            <button key={tab.id}
              onClick={() => setActiveSettingsTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeSettingsTab === tab.id
                  ? 'border-orange-500 text-orange-700 bg-white rounded-t-lg'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
              {tab.badge !== undefined && (
                <span className={`ml-1 inline-flex items-center justify-center h-4.5 min-w-[18px] px-1 rounded-full text-[10px] font-bold ${
                  activeSettingsTab === tab.id ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600'
                }`}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 min-h-0 overflow-hidden">

          {/* ===== COLUMNS TAB ===== */}
          {activeSettingsTab === 'columns' && (
            <div className="flex h-full min-h-0">
              {/* LEFT: Available Columns */}
              <div className="w-1/2 border-r border-gray-200 flex flex-col">
                <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Available ({availableIds.length})
                  </span>
                </div>
                <div className="flex-1 overflow-auto"
                  onDragOver={handleAvailableListDragOver}
                  onDrop={handleAvailableDrop}>
                  {availableIds.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">All columns are visible</p>
                  ) : (
                    availableIds.map(id => (
                      <button key={id}
                        type="button"
                        aria-label={`Show ${colLabel(id)}`}
                        draggable
                        onDragStart={() => handleAvailableDragStart(id)}
                        onDragEnd={handleDragEnd}
                        className="w-full text-left border-0 bg-transparent flex items-center gap-2 px-4 py-2 border-b border-gray-50 hover:bg-orange-50 cursor-grab active:cursor-grabbing group transition-colors"
                        onClick={() => moveToVisible(id)}>
                        <GripVertical className="h-3.5 w-3.5 text-gray-300" />
                        <EyeOff className="h-3 w-3 text-gray-300" />
                        <span className="text-sm text-gray-700 flex-1">{colLabel(id)}</span>
                        <Plus className="h-3.5 w-3.5 text-gray-300 group-hover:text-orange-500 transition-colors" />
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* RIGHT: Visible Columns */}
              <div className="w-1/2 flex flex-col">
                <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                    Visible ({visibleIds.length})
                  </span>
                </div>
                <div className="flex-1 overflow-auto"
                  onDragOver={handleVisibleListDragOver}
                  onDrop={handleVisibleDrop}>
                  {visibleIds.map((id, idx) => (
                    <React.Fragment key={id}>
                      {colDropTarget === idx && colDragSource && !(colDragSource.type === 'visible' && colDragSource.idx === idx) && (
                        <InsertionLine />
                      )}
                      <div
                        draggable
                        onDragStart={() => handleVisibleDragStart(idx)}
                        onDragOver={(e) => handleVisibleDragOver(e, idx)}
                        onDrop={(e) => { e.stopPropagation(); handleVisibleDrop(); }}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-2 px-4 py-2 border-b border-gray-50 cursor-grab active:cursor-grabbing group transition-colors ${
                          colDragSource?.type === 'visible' && colDragSource.idx === idx ? 'opacity-30 bg-gray-50' : 'hover:bg-gray-50'
                        }`}>
                        <GripVertical className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500" />
                        <span className="text-[10px] font-bold text-gray-400 w-4 text-right shrink-0">{idx + 1}</span>
                        <Eye className="h-3 w-3 text-orange-400" />
                        <span className="text-sm text-gray-700 flex-1">{colLabel(id)}</span>
                        <button type="button" aria-label={`Hide ${colLabel(id)}`} onClick={(e) => { e.stopPropagation(); moveToAvailable(id); }}
                          className="p-0.5 text-gray-300 hover:text-red-500 rounded-sm transition-colors opacity-0 group-hover:opacity-100">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </React.Fragment>
                  ))}
                  {/* Insertion line at end */}
                  {colDropTarget === visibleIds.length && colDragSource && (
                    <InsertionLine />
                  )}
                  {/* Empty drop zone at bottom */}
                  {visibleIds.length === 0 && (
                    <div className="flex items-center justify-center py-8 text-xs text-gray-400">
                      <Plus className="h-4 w-4 mr-1" /> Drop columns here
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ===== SORTING TAB ===== */}
          {activeSettingsTab === 'sorting' && (
            <div className="flex flex-col h-full min-h-0">
              {/* Add sort */}
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <select
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    value=""
                    onChange={(e) => { if (e.target.value) addSort(e.target.value); }}>
                    <option value="">Add a sort field...</option>
                    {sortableFields.map(c => (
                      <option key={c.id} value={c.id}>
                        {typeof c.header === 'string' ? c.header : c.id}
                        {localVis[c.id] === false ? ' (hidden)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {localSorting.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1.5">Drag to reorder priority. First sort has highest priority.</p>
                )}
              </div>

              {/* Sort list */}
              <div className="flex-1 overflow-auto">
                {localSorting.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <ArrowUpDown className="h-8 w-8 mb-2 text-gray-300" />
                    <p className="text-sm font-medium">No sorting applied</p>
                    <p className="text-xs mt-1">Select a field above to add a sort</p>
                  </div>
                ) : (
                  localSorting.map((sort, idx) => (
                    <React.Fragment key={sort.id}>
                      {sortDropTarget === idx && sortDragIdx !== null && sortDragIdx !== idx && (
                        <InsertionLine />
                      )}
                      <div
                        draggable
                        onDragStart={() => setSortDragIdx(idx)}
                        onDragOver={(e) => handleSortDragOver(e, idx)}
                        onDrop={(e) => { e.stopPropagation(); handleSortDrop(); }}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 px-5 py-2.5 border-b border-gray-50 cursor-grab active:cursor-grabbing group transition-colors ${
                          sortDragIdx === idx ? 'opacity-30 bg-gray-50' : 'hover:bg-gray-50'
                        }`}>
                        <GripVertical className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500" />
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700 flex-1">{colLabel(sort.id)}</span>
                        {localVis[sort.id] === false && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-500 font-medium">hidden</span>
                        )}
                        <button
                          onClick={() => toggleSortDir(idx)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                            sort.desc
                              ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}>
                          {sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                          {sort.desc ? 'DESC' : 'ASC'}
                        </button>
                        <button onClick={() => removeSort(idx)}
                          className="p-0.5 text-gray-300 hover:text-red-500 rounded-sm transition-colors opacity-0 group-hover:opacity-100">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </React.Fragment>
                  ))
                )}
                {/* Insertion line at end of sort list */}
                {sortDropTarget === localSorting.length && sortDragIdx !== null && (
                  <InsertionLine />
                )}
              </div>
            </div>
          )}

          {/* ===== GROUPING TAB ===== */}
          {activeSettingsTab === 'grouping' && (
            <div className="flex flex-col h-full min-h-0">
              {/* Add group */}
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <select
                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    value=""
                    onChange={(e) => { if (e.target.value) addGroup(e.target.value); }}>
                    <option value="">Add a group field...</option>
                    {groupableFields.map(c => (
                      <option key={c.id} value={c.id}>{typeof c.header === 'string' ? c.header : c.id}</option>
                    ))}
                  </select>
                </div>
                {localGroupBy.length > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1.5">Drag to reorder priority. First group is the outermost level.</p>
                )}
              </div>

              {/* Group list */}
              <div className="flex-1 overflow-auto">
                {localGroupBy.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <Layers className="h-8 w-8 mb-2 text-gray-300" />
                    <p className="text-sm font-medium">No grouping applied</p>
                    <p className="text-xs mt-1">Select a field above to group rows</p>
                  </div>
                ) : (
                  localGroupBy.map((gId, idx) => (
                    <React.Fragment key={gId}>
                      {groupDropTarget === idx && groupDragIdx !== null && groupDragIdx !== idx && (
                        <InsertionLine />
                      )}
                      <div
                        draggable
                        onDragStart={() => setGroupDragIdx(idx)}
                        onDragOver={(e) => handleGroupDragOver(e, idx)}
                        onDrop={(e) => { e.stopPropagation(); handleGroupDrop(); }}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 px-5 py-2.5 border-b border-gray-50 cursor-grab active:cursor-grabbing group transition-colors ${
                          groupDragIdx === idx ? 'opacity-30 bg-gray-50' : 'hover:bg-gray-50'
                        }`}>
                        <GripVertical className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500" />
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-gray-700 flex-1">{colLabel(gId)}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-600 font-medium">
                          Level {idx + 1}
                        </span>
                        <button onClick={() => removeGroup(idx)}
                          className="p-0.5 text-gray-300 hover:text-red-500 rounded-sm transition-colors opacity-0 group-hover:opacity-100">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </React.Fragment>
                  ))
                )}
                {/* Insertion line at end of group list */}
                {groupDropTarget === localGroupBy.length && groupDragIdx !== null && (
                  <InsertionLine />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span>{visibleIds.length} columns</span>
            {localSorting.length > 0 && <span>· {localSorting.length} sort{localSorting.length > 1 ? 's' : ''}</span>}
            {localGroupBy.length > 0 && <span>· {localGroupBy.length} group{localGroupBy.length > 1 ? 's' : ''}</span>}
          </div>
          <button onClick={handleDone}
            className="px-5 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Query Builder Modal
// ============================================================

const OPERATOR_LABELS: Record<QueryOperator, string> = {
  equals: 'Equals', notEquals: 'Not equals',
  contains: 'Contains', notContains: 'Does not contain',
  startsWith: 'Starts with', endsWith: 'Ends with',
  greaterThan: 'Greater than', lessThan: 'Less than',
  greaterThanOrEqual: '≥', lessThanOrEqual: '≤',
  isEmpty: 'Is empty', isNotEmpty: 'Is not empty',
  between: 'Between',
};

const NO_VALUE_OPS: QueryOperator[] = ['isEmpty', 'isNotEmpty'];

let _qbIdCounter = 0;
function qbId() { return `qb_${++_qbIdCounter}`; }

function emptyCondition(field: string): QueryCondition {
  return { id: qbId(), field, operator: 'contains', value: '' };
}

function emptyGroup(): QueryGroup {
  return { id: qbId(), logic: 'AND', conditions: [] };
}

function QueryConditionRow<T>({
  condition, columns, data, onChange, onRemove,
}: {
  condition: QueryCondition;
  columns: DataGridColumn<T>[];
  data: T[];
  onChange: (c: QueryCondition) => void;
  onRemove: () => void;
}) {
  const needsValue = !NO_VALUE_OPS.includes(condition.operator);

  // Extract unique values for the selected field from the actual data
  const uniqueValues = useMemo(() => {
    const col = columns.find(c => c.id === condition.field);
    if (!col) return [];
    const seen = new Set<string>();
    data.forEach(row => {
      const raw = col.accessorFn ? col.accessorFn(row) : (col.accessorKey ? (row as any)[col.accessorKey] : '');
      if (raw != null && String(raw).trim() !== '') seen.add(String(raw));
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [condition.field, columns, data]);

  return (
    <div className="flex items-center gap-2">
      <select value={condition.field}
        onChange={(e) => onChange({ ...condition, field: e.target.value, value: '' })}
        className="px-2 py-1.5 border border-gray-300 rounded-sm text-sm min-w-[140px]">
        {columns.filter(c => c.id !== '_actions' && c.id !== '_select').map(c => (
          <option key={c.id} value={c.id}>{c.header}</option>
        ))}
      </select>
      <select value={condition.operator}
        onChange={(e) => onChange({ ...condition, operator: e.target.value as QueryOperator })}
        className="px-2 py-1.5 border border-gray-300 rounded-sm text-sm min-w-[130px]">
        {Object.entries(OPERATOR_LABELS).map(([k, v]) => (
          <option key={k} value={k}>{v}</option>
        ))}
      </select>
      {needsValue && (
        uniqueValues.length > 0 && uniqueValues.length <= 100 ? (
          <div className="flex-1 min-w-[180px] flex items-center gap-1">
            <select
              value={condition.value}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded-sm text-sm">
              <option value="">Select value...</option>
              {uniqueValues.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        ) : (
          <input type="text" value={condition.value}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            placeholder="Value..."
            className="px-2 py-1.5 border border-gray-300 rounded-sm text-sm flex-1 min-w-[120px]" />
        )
      )}
      {condition.operator === 'between' && (
        uniqueValues.length > 0 && uniqueValues.length <= 100 ? (
          <select
            value={condition.value2 || ''}
            onChange={(e) => onChange({ ...condition, value2: e.target.value })}
            className="px-2 py-1.5 border border-gray-300 rounded-sm text-sm flex-1 min-w-[120px]">
            <option value="">Select value...</option>
            {uniqueValues.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        ) : (
          <input type="text" value={condition.value2 || ''}
            onChange={(e) => onChange({ ...condition, value2: e.target.value })}
            placeholder="Value 2..."
            className="px-2 py-1.5 border border-gray-300 rounded-sm text-sm flex-1 min-w-[120px]" />
        )
      )}
      <button onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500 rounded-sm">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function QueryGroupEditor<T>({
  group, columns, data, onChange, onRemove, depth,
}: {
  group: QueryGroup;
  columns: DataGridColumn<T>[];
  data: T[];
  onChange: (g: QueryGroup) => void;
  onRemove?: () => void;
  depth: number;
}) {
  const updateItem = (idx: number, item: QueryCondition | QueryGroup) => {
    const next = [...group.conditions];
    next[idx] = item;
    onChange({ ...group, conditions: next });
  };
  const removeItem = (idx: number) => {
    onChange({ ...group, conditions: group.conditions.filter((_, i) => i !== idx) });
  };
  const firstField = columns.find(c => c.id !== '_actions' && c.id !== '_select')?.id || '';

  return (
    <div className={`border rounded-lg p-3 ${depth === 0 ? 'border-gray-200' : 'border-gray-300 bg-gray-50'}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-medium text-gray-500 uppercase">Match</span>
        <select value={group.logic}
          onChange={(e) => onChange({ ...group, logic: e.target.value as 'AND' | 'OR' })}
          className="px-2 py-1 border border-gray-300 rounded-sm text-sm font-medium">
          <option value="AND">ALL (AND)</option>
          <option value="OR">ANY (OR)</option>
        </select>
        <span className="text-xs text-gray-400">of the following</span>
        <div className="flex-1" />
        {onRemove && (
          <button onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500 rounded-sm">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="space-y-2">
        {group.conditions.map((item, idx) => (
          <div key={isQueryGroup(item) ? item.id : item.id}>
            {idx > 0 && (
              <div className="flex items-center gap-2 py-1">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs font-medium text-gray-400">{group.logic}</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
            )}
            {isQueryGroup(item) ? (
              <QueryGroupEditor group={item} columns={columns} data={data}
                onChange={(g) => updateItem(idx, g)}
                onRemove={() => removeItem(idx)}
                depth={depth + 1} />
            ) : (
              <QueryConditionRow condition={item} columns={columns} data={data}
                onChange={(c) => updateItem(idx, c)}
                onRemove={() => removeItem(idx)} />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => onChange({ ...group, conditions: [...group.conditions, emptyCondition(firstField)] })}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 rounded-sm transition-colors">
          <Plus className="h-3 w-3" /> Add condition
        </button>
        {depth < 2 && (
          <button onClick={() => onChange({ ...group, conditions: [...group.conditions, emptyGroup()] })}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100 rounded-sm transition-colors">
              <Plus className="h-3 w-3" /> Add group
          </button>
        )}
      </div>
    </div>
  );
}

function QueryBuilderModal<T>({
  columns, data, query, onApply, onClose,
}: {
  columns: DataGridColumn<T>[];
  data: T[];
  query: QueryGroup;
  onApply: (q: QueryGroup) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<QueryGroup>(query);

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Query Builder</h2>
            <p className="text-sm text-gray-500">Build complex filters with AND/OR logic</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          <QueryGroupEditor group={draft} columns={columns} data={data} onChange={setDraft} depth={0} />
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button onClick={() => { setDraft(emptyGroup()); }}
            className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            Clear all
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={() => { onApply(draft); onClose(); }}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors">
              Apply Query
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Query evaluation (client-side)
// ============================================================

function evaluateQuery<T>(row: T, query: QueryGroup, columns: DataGridColumn<T>[]): boolean {
  if (query.conditions.length === 0) return true;

  const results = query.conditions.map(item => {
    if (isQueryGroup(item)) return evaluateQuery(row, item, columns);
    return evaluateCondition(row, item, columns);
  });

  return query.logic === 'AND' ? results.every(Boolean) : results.some(Boolean);
}

function evaluateCondition<T>(row: T, cond: QueryCondition, columns: DataGridColumn<T>[]): boolean {
  const col = columns.find(c => c.id === cond.field);
  if (!col) return true;
  const raw = col.accessorFn ? col.accessorFn(row) : (col.accessorKey ? (row as any)[col.accessorKey] : '');
  const cellStr = raw == null ? '' : String(raw).toLowerCase();
  const val = cond.value.toLowerCase();

  switch (cond.operator) {
    case 'equals': return cellStr === val;
    case 'notEquals': return cellStr !== val;
    case 'contains': return cellStr.includes(val);
    case 'notContains': return !cellStr.includes(val);
    case 'startsWith': return cellStr.startsWith(val);
    case 'endsWith': return cellStr.endsWith(val);
    case 'greaterThan': return cellStr > val;
    case 'lessThan': return cellStr < val;
    case 'greaterThanOrEqual': return cellStr >= val;
    case 'lessThanOrEqual': return cellStr <= val;
    case 'isEmpty': return cellStr === '';
    case 'isNotEmpty': return cellStr !== '';
    case 'between': return cellStr >= val && cellStr <= (cond.value2 || '').toLowerCase();
    default: return true;
  }
}

// ============================================================
// Export helpers
// ============================================================

type ExportFormat = 'csv' | 'xlsx' | 'pdf';

function getExportCols<T>(columns: DataGridColumn<T>[]) {
  return columns.filter(c => c.pinned !== 'right' && c.id !== '_actions');
}

function getExportData<T>(rows: T[], columns: DataGridColumn<T>[]) {
  const exportCols = getExportCols(columns);
  const headers = exportCols.map(c => typeof c.header === 'string' ? c.header : c.id);
  const data = rows.map(row => exportCols.map(c => {
    const val = c.accessorFn ? c.accessorFn(row) : (c.accessorKey ? (row as any)[c.accessorKey] : '');
    return val == null ? '' : String(val);
  }));
  return { headers, data };
}

function exportCSV<T>(rows: T[], columns: DataGridColumn<T>[], filename: string) {
  const { headers, data } = getExportData(rows, columns);
  downloadBlob(new Blob([buildCsv(headers, data)], { type: 'text/csv' }), filename);
}

function escapeXml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function columnName(index: number) {
  let name = '';
  let current = index + 1;
  while (current > 0) {
    const mod = (current - 1) % 26;
    name = String.fromCharCode(65 + mod) + name;
    current = Math.floor((current - mod) / 26);
  }
  return name;
}

function createXlsxBlob(headers: string[], data: string[][]) {
  const allRows = [headers, ...data];
  const widths = headers.map((h, i) => Math.min(Math.max(h.length, ...data.map(row => (row[i] || '').length)) + 2, 50));
  const colsXml = `<cols>${widths.map((width, index) => `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`).join('')}</cols>`;
  const rowsXml = allRows.map((row, rowIndex) => {
    const rowNumber = rowIndex + 1;
    const cells = row.map((value, columnIndex) => {
      const ref = `${columnName(columnIndex)}${rowNumber}`;
      return `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
    }).join('');
    return `<row r="${rowNumber}">${cells}</row>`;
  }).join('');
  const worksheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${colsXml}<sheetData>${rowsXml}</sheetData></worksheet>`;
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/></Types>'),
    '_rels/.rels': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>'),
    'xl/workbook.xml': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Export" sheetId="1" r:id="rId1"/></sheets></workbook>'),
    'xl/_rels/workbook.xml.rels': strToU8('<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>'),
    'xl/worksheets/sheet1.xml': strToU8(worksheet),
  };
  const archive = zipSync(files);
  const body = archive.buffer.slice(archive.byteOffset, archive.byteOffset + archive.byteLength) as ArrayBuffer;
  return new Blob([body], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function exportXLSX<T>(rows: T[], columns: DataGridColumn<T>[], filename: string) {
  const { headers, data } = getExportData(rows, columns);
  downloadBlob(createXlsxBlob(headers, data), filename);
}

function exportPDF<T>(rows: T[], columns: DataGridColumn<T>[], filename: string) {
  const { headers, data } = getExportData(rows, columns);
  const doc = new jsPDF({ orientation: headers.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(12);
  doc.text(filename.replace(/\.pdf$/i, ''), 14, 15);
  autoTable(doc, {
    head: [headers],
    body: data,
    startY: 22,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [249, 115, 22], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [255, 247, 237] },
    margin: { top: 10, left: 10, right: 10 },
  });
  doc.save(filename);
}

function runExport<T>(format: ExportFormat, rows: T[], columns: DataGridColumn<T>[], gridId: string) {
  const base = `${gridId}-export`;
  switch (format) {
    case 'csv': exportCSV(rows, columns, `${base}.csv`); break;
    case 'xlsx': exportXLSX(rows, columns, `${base}.xlsx`); break;
    case 'pdf': exportPDF(rows, columns, `${base}.pdf`); break;
  }
}

const EXPORT_FORMATS: { id: ExportFormat; label: string; icon: typeof FileText }[] = [
  { id: 'csv',  label: 'CSV (.csv)',   icon: FileText },
  { id: 'xlsx', label: 'Excel (.xlsx)', icon: FileSpreadsheet },
  { id: 'pdf',  label: 'PDF (.pdf)',   icon: FileDown },
];

// ============================================================
// Main DataGrid Component
// ============================================================

const ALL_ROWS_PAGE_SIZE = 99999;

// Stable default references — inline defaults (e.g. `filters = []`) create NEW
// arrays every render, breaking useMemo deps and causing infinite re-render loops.
const EMPTY_FILTERS: DataGridFilter[] = [];
const EMPTY_BULK_ACTIONS: BulkAction<any>[] = [];
const EMPTY_ROW_ACTIONS: DataGridRowAction<any>[] = [];
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EMPTY_COLUMN_FILTER_TYPES: DataGridProps<any>['columnFilterTypes'] = {};

function columnVisibilityEqual(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((id) => a[id] === b[id]);
}

/**
 * @component DateFilterInput
 * Purpose: Date filter-row input that applies on blur/Enter only (not per
 *          keystroke) and resyncs its draft whenever the column filter changes
 *          externally (chip removal, Clear all, saved views) so a stale date is
 *          never re-applied.
 * Props: value (current column filter value, '' when unset), onApply (called
 *        with the draft on blur/Enter when it differs from value).
 */
function DateFilterInput({ value, onApply }: { value: string; onApply: (value: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const apply = (next: string) => { if (next !== value) onApply(next); };
  return (
    <input
      type="date"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => apply(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') apply((e.target as HTMLInputElement).value); }}
      className="w-full px-2 py-1 border border-gray-200 rounded-sm text-xs focus:ring-1 focus:ring-orange-500 focus:border-orange-500" />
  );
}

/**
 * @component DataGrid
 * Purpose: Render a reusable table/grid with generic row actions and optional host-controlled drag/drop.
 * Props: Data or paginated fetch callback, column definitions, row actions, and grid state callbacks.
 * Behavior: Supports pointer and keyboard row-action menus; Escape returns focus to the opener or grid.
 * Data Source: local (rows are supplied by the caller's local-first service/hook).
 * Constraints: Generic presentation only; row action and drag/drop callbacks own domain authorization/mutation.
 * Performance: Client-side pagination or caller-controlled server paging; layout state is memoized.
 * @reusable
 * Usage: Shared by tenant-web and system-web grid pages.
 * Rules: Keep rows outside the tab order; preserve host-owned action descriptors and drag/drop policy.
 */
export default function DataGrid<T>({

  gridId,
  columns,
  data: clientData,
  fetchData,
  getRowId,
  searchPlaceholder = 'Search...',
  filters = EMPTY_FILTERS,
  toolbarActions,
  toolbarLeftExtra,
  exportable = false,
  selectable = false,
  bulkActions = EMPTY_BULK_ACTIONS,
  onSelectionChange,
  onRowClick,
  rowActions = EMPTY_ROW_ACTIONS,
  rowDragDrop,
  showRowChevron,
  activeRowId,
  emptyIcon,
  emptyTitle,
  emptySubtitle,
  emptyCTA,
  defaultPageSize = 25,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  stickyHeader = true,
  containerClassName,
  refreshKey,
  columnFilterTypes = EMPTY_COLUMN_FILTER_TYPES,
  expandedRowId,
  onRowExpand,
  expandedRowRender,
  initialState: initialStateProp,
  contextualColumnDefaults,
  onStateChange,
  onFilteredCountChange,
  isTier2 = true,
}: DataGridProps<T>) {
  // --- State ---
  const [sorting, setSorting] = useState<SortingState>(() => initialStateProp?.sorting ?? []);
  const [globalFilter, setGlobalFilter] = useState(() => (initialStateProp?.searchText ?? '').trim());
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => initialStateProp?.columnFilters ?? []);
  // A restored column filter must stay visible AND editable — reopen the filter
  // row on mount so persisted values are not silently applied from a hidden row.
  const [showColumnFilters, setShowColumnFilters] = useState(() => (initialStateProp?.columnFilters?.length ?? 0) > 0);
  const [filterValues, setFilterValues] = useState<Record<string, any>>(() => {
    if (initialStateProp?.filterValues && Object.keys(initialStateProp.filterValues).length > 0) return initialStateProp.filterValues;
    const defaults: Record<string, any> = {};
    filters.forEach(f => { if (f.defaultValue != null) defaults[f.id] = f.defaultValue; });
    return defaults;
  });
  const [contextualColumnOverrides, setContextualColumnOverrides] = useState<Record<string, boolean>>(
    () => readContextualColumnVisibilityOverrides(gridId),
  );
  const suppliedColumnIdsRef = useRef<Set<string> | null>(null);
  if (suppliedColumnIdsRef.current === null) suppliedColumnIdsRef.current = new Set(columns.map(({ id }) => id));
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const reconcileVisibility = (
      stored: Record<string, boolean>,
      retainFrom: Record<string, boolean> = stored,
    ) => retainUnsuppliedColumnLayout({
      visibility: applyContextualColumnDefaults(
        reconcileColumnLayout(columns, stored).visibility,
        contextualColumnDefaults,
        contextualColumnOverrides,
      ),
      order: [],
    }, { visibility: retainFrom }, suppliedColumnIdsRef.current!).visibility;
    // An explicit `columnVisibility` (including `{}`) means "use this template /
    // reset", not "fall through to the previous layout in localStorage".
    if (initialStateProp && Object.prototype.hasOwnProperty.call(initialStateProp, 'columnVisibility')) {
      const provided = initialStateProp.columnVisibility ?? {};
      const knownIds = columns.map(({ id }) => id).filter((id) => id !== '_actions' && id !== '_select');
      const hiddenDefaults = Object.fromEntries(
        columns.filter(({ hidden, id }) => hidden && id !== '_actions' && id !== '_select').map(({ id }) => [id, false]),
      );
      const templateVisibility = Object.keys(provided).length > 0
        ? resolveTemplateColumnVisibility(provided, knownIds, hiddenDefaults)
        : provided;
      return reconcileVisibility(templateVisibility, provided);
    }
    try {
      const stored = localStorage.getItem(gridColumnVisibilityKey(gridId));
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return reconcileVisibility(parsed);
        }
      }
    } catch { /* ignore */ }
    return reconcileVisibility({});
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const lastSelectedRowIndex = useRef<number>(-1);
  const rowDragSourceIdRef = useRef<string | null>(null);
  const suppressNextRowClickAfterDragRef = useRef(false);
  const suppressNextRowDoubleClickAfterDragRef = useRef(false);
  const [rowDropTarget, setRowDropTarget] = useState<{
    rowId: string;
    state: { allowed: boolean; reason?: string };
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [showManageColumns, setShowManageColumns] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (initialStateProp && Object.prototype.hasOwnProperty.call(initialStateProp, 'columnOrder')) {
      const providedOrder = initialStateProp.columnOrder ?? [];
      return retainUnsuppliedColumnLayout(
        reconcileColumnLayout(columns, {}, providedOrder),
        { order: providedOrder },
        suppliedColumnIdsRef.current!,
      ).order;
    }
    try {
      const stored = localStorage.getItem(gridColumnOrderKey(gridId));
      if (stored) {
        const parsed = JSON.parse(stored) as unknown;
        if (Array.isArray(parsed)) {
          const parsedOrder = parsed.filter((id): id is string => typeof id === 'string');
          return retainUnsuppliedColumnLayout(
            reconcileColumnLayout(columns, {}, parsedOrder),
            { order: parsedOrder },
            suppliedColumnIdsRef.current!,
          ).order;
        }
      }
    } catch { /* ignore */ }
    return reconcileColumnLayout(columns).order;
  });
  const [actionMenuRowId, setActionMenuRowId] = useState<string | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [actionSubmenu, setActionSubmenu] = useState<{
    actionId: string;
    top: number;
    left: number;
    actions: DataGridRowAction<T>[];
  } | null>(null);
  const actionMenuPayloadRef = useRef<{ row: T; actions: DataGridRowAction<T>[] } | null>(null);
  const [outsideDropFeedback, setOutsideDropFeedback] = useState<{ rowId: string; state: { allowed: boolean; reason?: string } } | null>(null);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [headerContextMenu, setHeaderContextMenu] = useState<{ x: number; y: number; columnId: string } | null>(null);
  const [groupByColumns, setGroupByColumns] = useState<string[]>(() => initialStateProp?.groupByColumns ?? []);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const gridRootRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const actionMenuFocusReturnRef = useRef<{ opener: HTMLElement | null; fallback: HTMLElement | null } | null>(null);
  const actionMenuFocusRestorePendingRef = useRef(false);
  const submenuFocusPendingRef = useRef(false);
  const menuAutoCloseTimer = useRef<ReturnType<typeof setTimeout>>();
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);

  const getGridRowElement = useCallback((rowId: string) => {
    const rows = tableRef.current?.querySelectorAll<HTMLElement>('[data-grid-row-id]');
    return Array.from(rows ?? []).find((element) => element.dataset.gridRowId === rowId) ?? null;
  }, []);

  const openActionMenu = useCallback((
    rowId: string,
    anchor: HTMLElement | CursorPoint | null,
    opener: HTMLElement | null,
    fallback: HTMLElement | null,
    visibleActionCount: number,
    rowValue: T,
    actions: DataGridRowAction<T>[],
    placement: PopupPlacement = 'bottom-end',
  ) => {
    const resolvedAnchor = anchor ?? tableRef.current;
    if (!resolvedAnchor) return;
    const { left, top } = clampPopupPosition(
      resolvedAnchor,
      { width: 192, estimatedHeight: visibleActionCount * MENU_ITEM_HEIGHT + MENU_PADDING_Y },
      placement,
      { offset: 4 },
    );
    actionMenuFocusReturnRef.current = { opener, fallback };
    actionMenuPayloadRef.current = { row: rowValue, actions };
    setActionSubmenu(null);
    setActionMenuPos({ top, left });
    setActionMenuRowId(rowId);
  }, []);

  const closeActionMenu = useCallback((restoreFocus: boolean) => {
    actionMenuFocusRestorePendingRef.current = restoreFocus;
    actionMenuPayloadRef.current = null;
    setActionSubmenu(null);
    if (!restoreFocus) actionMenuFocusReturnRef.current = null;
    setActionMenuRowId(null);
    setActionMenuPos(null);
  }, []);

  // Helper to close all popup menus at once
  const closeAllMenus = useCallback(() => {
    closeActionMenu(false);
    setShowColumnToggle(false);
    setShowOverflowMenu(false); setShowExportMenu(false); setHeaderContextMenu(null);
  }, [closeActionMenu]);

  const openActionSubmenu = useCallback((
    actionId: string,
    anchor: HTMLElement,
    actions: DataGridRowAction<T>[],
    focusFirst: boolean,
  ) => {
    const { left, top } = clampPopupPosition(
      anchor,
      { width: 192, estimatedHeight: actions.length * MENU_ITEM_HEIGHT + MENU_PADDING_Y },
      'right-start',
      { offset: 0 },
    );
    submenuFocusPendingRef.current = focusFirst;
    setActionSubmenu({ actionId, left, top, actions });
  }, []);

  const closeActionSubmenu = useCallback((restoreFocus: boolean) => {
    setActionSubmenu(null);
    submenuFocusPendingRef.current = false;
    if (restoreFocus) {
      actionMenuRef.current
        ?.querySelector<HTMLButtonElement>('[data-submenu-parent="true"]')
        ?.focus();
    }
  }, []);

  const openExpandedRowActions = useCallback((
    rowValue: T,
    event: React.SyntheticEvent<HTMLElement>,
    suppliedActions?: DataGridRowAction<T>[],
  ) => {
    const menuActions = suppliedActions ?? rowActions.filter((action) => action.expandedOnly);
    const visibleActions = visibleRowActions(menuActions, rowValue);
    if (visibleActions.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    closeAllMenus();
    const rowId = getRowId(rowValue);
    openActionMenu(
      rowId,
      event.currentTarget,
      event.currentTarget,
      getGridRowElement(rowId) ?? tableRef.current,
      visibleActions.length,
      rowValue,
      visibleActions,
    );
  }, [closeAllMenus, getGridRowElement, getRowId, openActionMenu, rowActions]);

  useEffect(() => {
    if (!actionMenuRowId || !actionMenuPos) return;
    actionMenuRef.current?.querySelector<HTMLButtonElement>('[data-row-action-menu-level="root"] [role="menuitem"]:not(:disabled)')?.focus();
  }, [actionMenuRowId, actionMenuPos]);

  useEffect(() => {
    if (!actionSubmenu || !submenuFocusPendingRef.current) return;
    submenuFocusPendingRef.current = false;
    actionMenuRef.current
      ?.querySelector<HTMLButtonElement>('[data-row-action-menu-level="submenu"] [role="menuitem"]:not(:disabled)')
      ?.focus();
  }, [actionSubmenu]);

  useEffect(() => {
    if (actionMenuRowId !== null || !actionMenuFocusRestorePendingRef.current) return;
    actionMenuFocusRestorePendingRef.current = false;
    const focusReturn = actionMenuFocusReturnRef.current;
    actionMenuFocusReturnRef.current = null;
    const fallbackRowId = focusReturn?.fallback?.matches('tr[data-grid-row-id]')
      ? focusReturn.fallback.dataset.gridRowId
      : undefined;
    const fallbackRow = fallbackRowId ? getGridRowElement(fallbackRowId) ?? focusReturn?.fallback : null;
    const fallbackActionButton = fallbackRow?.querySelector<HTMLElement>('button[aria-label="Row actions"]');
    const target = focusReturn?.opener?.isConnected
      ? focusReturn.opener
      : fallbackActionButton?.isConnected
        ? fallbackActionButton
        : fallbackRow?.isConnected
          ? fallbackRow
          : focusReturn?.fallback?.isConnected
            ? focusReturn.fallback
            : tableRef.current ?? gridRootRef.current;
    target?.focus();
  }, [actionMenuRowId, getGridRowElement]);

  const handleActionMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const currentMenu = target?.closest<HTMLElement>('[data-row-action-menu-level]');
    const isSubmenu = currentMenu?.dataset.rowActionMenuLevel === 'submenu';
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (isSubmenu) closeActionSubmenu(true);
      else closeActionMenu(true);
      return;
    }
    if (event.key === 'ArrowLeft' && isSubmenu) {
      event.preventDefault();
      event.stopPropagation();
      closeActionSubmenu(true);
      return;
    }
    if (event.key === 'ArrowRight' && target?.dataset.actionHasChildren === 'true') {
      event.preventDefault();
      event.stopPropagation();
      const actionId = target.dataset.actionId;
      const row = actionMenuPayloadRef.current?.row;
      const allActions = actionMenuPayloadRef.current?.actions ?? rowActions;
      const action = allActions.find((candidate) => candidate.id === actionId);
      if (action && row) {
        const children = visibleRowActions<T>(action.children ?? [], row);
        if (children.length > 0) openActionSubmenu(action.id, target, children, true);
      }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from(currentMenu?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])
      .filter((item) => item.closest('[data-row-action-menu-level]') === currentMenu);
    if (items.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex < 0
      ? direction > 0 ? 0 : items.length - 1
      : (currentIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  }, [closeActionMenu, closeActionSubmenu, openActionSubmenu, rowActions]);

  const isServerMode = !!fetchData;
  const [isScrollMode, setIsScrollMode] = useState(() => initialStateProp?.pageSize === 99999);
  const [currentPageSize, setCurrentPageSize] = useState(() => {
    if (initialStateProp?.pageSize && initialStateProp.pageSize !== 99999) return initialStateProp.pageSize;
    return defaultPageSize;
  });
  const [pageIndex, setPageIndex] = useState(0);
  const [showQueryBuilder, setShowQueryBuilder] = useState(false);
  const [activeQuery, setActiveQuery] = useState<QueryGroup>(() => initialStateProp?.activeQuery ?? emptyGroup());
  const [serverData, setServerData] = useState<T[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverError, setServerError] = useState<Error | null>(null);
  const fetchSeq = useRef(0);
  const hasLoadedServerData = useRef(false);

  // Debounced search
  const [searchInput, setSearchInput] = useState(() => (initialStateProp?.searchText ?? '').trim());
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  const clickTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback((val: string) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    // Apply the trimmed value so whitespace-only input never filters the grid
    // (an untrimmed ' ' would narrow rows while producing no filter chip).
    searchTimer.current = setTimeout(() => setGlobalFilter(val.trim()), 300);
  }, []);
  const clearSearch = useCallback(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = undefined;
    setSearchInput('');
    setGlobalFilter('');
  }, []);

  const columnSchemaKey = JSON.stringify(columns.map(({ id, hidden }) => [id, hidden]));
  const contextualDefaultsKey = JSON.stringify(contextualColumnDefaults ?? {});
  const contextualOverridesKey = JSON.stringify(contextualColumnOverrides);
  useEffect(() => {
    for (const { id } of columns) suppliedColumnIdsRef.current!.add(id);
    setColumnVisibility((current) => {
      const reconciled = reconcileColumnLayout(columns, current, []).visibility;
      const resolved = applyContextualColumnDefaults(
        reconciled,
        contextualColumnDefaults,
        contextualColumnOverrides,
      );
      const retained = retainUnsuppliedColumnLayout(
        { visibility: resolved, order: [] },
        { visibility: current },
        suppliedColumnIdsRef.current!,
      ).visibility;
      return columnVisibilityEqual(current, retained) ? current : retained;
    });
    setColumnOrder((current) => {
      const next = retainUnsuppliedColumnLayout(
        reconcileColumnLayout(columns, {}, current),
        { order: current },
        suppliedColumnIdsRef.current!,
      ).order;
      return current.length === next.length && current.every((id, index) => id === next[index])
        ? current
        : next;
    });
  }, [columnSchemaKey, contextualDefaultsKey, contextualOverridesKey]);

  // Persist column visibility and order to localStorage
  useEffect(() => {
    localStorage.setItem(gridColumnVisibilityKey(gridId), JSON.stringify(columnVisibility));
  }, [columnVisibility, gridId]);
  useEffect(() => {
    localStorage.setItem(gridColumnOrderKey(gridId), JSON.stringify(columnOrder));
  }, [columnOrder, gridId]);

  const persistContextualOverrides = (
    visibility: Record<string, boolean>,
    editedIds: string[],
    resetToFactory = false,
  ): Record<string, boolean> => {
    const next: Record<string, boolean> = resetToFactory ? {} : { ...contextualColumnOverrides };
    let changed = resetToFactory;
    for (const id of editedIds) {
      if (
        Object.prototype.hasOwnProperty.call(contextualColumnDefaults ?? {}, id)
        && Object.prototype.hasOwnProperty.call(visibility, id)
      ) {
        next[id] = visibility[id];
        changed = true;
      }
    }
    if (!changed) return contextualColumnOverrides;
    writeContextualColumnVisibilityOverrides(gridId, next);
    setContextualColumnOverrides(next);
    return next;
  };

  const applyUserColumnVisibility = (
    visibility: Record<string, boolean>,
    editedIds: string[],
    resetToFactory = false,
  ): Record<string, boolean> => {
    const reconciled = reconcileColumnLayout(columns, visibility, columnOrder).visibility;
    const overrides = persistContextualOverrides(reconciled, editedIds, resetToFactory);
    const resolved = applyContextualColumnDefaults(reconciled, contextualColumnDefaults, overrides);
    const retained = retainUnsuppliedColumnLayout(
      { visibility: resolved, order: [] },
      resetToFactory ? {} : { visibility: columnVisibility },
      suppliedColumnIdsRef.current!,
    ).visibility;
    setColumnVisibility(retained);
    return retained;
  };

  const handleColumnVisibilityChange = (
    updater: Record<string, boolean> | ((current: Record<string, boolean>) => Record<string, boolean>),
  ) => {
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    const editedIds = Object.keys(contextualColumnDefaults ?? {}).filter((id) => (
      Object.prototype.hasOwnProperty.call(next, id) && next[id] !== columnVisibility[id]
    ));
    applyUserColumnVisibility(next, editedIds);
  };

  const sortingKey = useMemo(() => JSON.stringify(sorting), [sorting]);
  const groupByKey = useMemo(() => JSON.stringify(groupByColumns), [groupByColumns]);
  // Effective sorting: prepend group-by columns so grouped rows are adjacent
  const effectiveSorting = useMemo(() => {
    if (groupByColumns.length === 0) return sorting;
    const groupSorts: SortingState = groupByColumns
      .filter(colId => !sorting.some(s => s.id === colId))
      .map(colId => ({ id: colId, desc: false }));
    return [...groupSorts, ...sorting];
  }, [sorting, groupByColumns]);
  const filterValuesKey = useMemo(() => JSON.stringify(filterValues), [filterValues]);
  const columnFiltersKey = useMemo(() => JSON.stringify(columnFilters), [columnFilters]);
  const activeQueryKey = useMemo(() => JSON.stringify(activeQuery), [activeQuery]);
  const serverCriteriaKey = useMemo(
    () => JSON.stringify({ globalFilter, filterValues, columnFilters, activeQuery, sorting }),
    [globalFilter, filterValues, columnFilters, activeQuery, sorting],
  );
  const prevServerCriteriaKey = useRef(serverCriteriaKey);

  useEffect(() => {
    if (!isServerMode) return;
    if (prevServerCriteriaKey.current === serverCriteriaKey) return;
    prevServerCriteriaKey.current = serverCriteriaKey;
    setPageIndex(0);
  }, [isServerMode, serverCriteriaKey]);

  useEffect(() => {
    if (!fetchData) return;
    const seq = ++fetchSeq.current;
    const sort = effectiveSorting[0];
    const mergedFilters: Record<string, any> = { ...filterValues };
    for (const filter of columnFilters) {
      if (filter.value == null || filter.value === '') continue;
      mergedFilters[filter.id] = filter.value;
    }
    const params: FetchParams = {
      page: pageIndex + 1,
      pageSize: isScrollMode ? ALL_ROWS_PAGE_SIZE : currentPageSize,
      sortBy: sort?.id,
      sortDirection: sort ? (sort.desc ? 'desc' : 'asc') : undefined,
      sorting: effectiveSorting.length > 0 ? effectiveSorting : undefined,
      search: globalFilter,
      filters: mergedFilters,
      query: activeQuery.conditions.length > 0 ? activeQuery : undefined,
    };

    setIsLoading(!hasLoadedServerData.current);
    setServerError(null);
    fetchData(params)
      .then((result) => {
        if (seq !== fetchSeq.current) return;
        hasLoadedServerData.current = true;
        setServerData(result.data ?? []);
        setServerTotal(result.total ?? 0);
      })
      .catch((err) => {
        if (seq !== fetchSeq.current) return;
        if (!hasLoadedServerData.current) {
          setServerData([]);
          setServerTotal(0);
        }
        setServerError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (seq === fetchSeq.current) setIsLoading(false);
      });
  }, [
    fetchData,
    pageIndex,
    currentPageSize,
    isScrollMode,
    globalFilter,
    sortingKey,
    groupByKey,
    filterValuesKey,
    columnFiltersKey,
    activeQueryKey,
    refreshKey,
    effectiveSorting,
  ]);

  // Notify parent of state changes for auto-save. Cancel the debounce on
  // cleanup / unmount so a pending snapshot from the previous layout cannot
  // overwrite a just-applied template or factory reset after remount.
  const stateChangeTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!onStateChange) return;
    clearTimeout(stateChangeTimer.current);
    stateChangeTimer.current = setTimeout(() => {
      onStateChange({
        columnVisibility,
        columnOrder,
        sorting,
        filterValues,
        columnFilters,
        pageSize: isScrollMode ? 99999 : currentPageSize,
        searchText: globalFilter,
        activeQuery: activeQuery.conditions.length > 0 ? activeQuery : undefined,
        groupByColumns,
      });
    }, 500);
    return () => {
      clearTimeout(stateChangeTimer.current);
    };
  }, [columnVisibility, columnOrder, sorting, filterValues, columnFilters, globalFilter, activeQuery, isScrollMode, currentPageSize, onStateChange, groupByColumns]);

  // Close menus on outside mousedown (left-button only — prevents right-click from dismissing)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (e.button !== 0) return; // only close on primary (left) button
      closeAllMenus();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [closeAllMenus]);

  // Auto-close context menus after 5 seconds of inactivity
  useEffect(() => {
    clearTimeout(menuAutoCloseTimer.current);
    if (headerContextMenu) {
      menuAutoCloseTimer.current = setTimeout(() => {
        closeAllMenus();
      }, 5000);
    }
    return () => clearTimeout(menuAutoCloseTimer.current);
  }, [headerContextMenu, closeAllMenus]);

  // Responsive toolbar — collapse when too narrow (with hysteresis to prevent oscillation)
  const lastCollapsed = useRef(false);
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    let rafId = 0;
    const observer = new ResizeObserver((entries) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        for (const entry of entries) {
          const w = entry.contentRect.width;
          // Hysteresis: collapse at <860, expand at >940 — prevents oscillation at the boundary
          const shouldCollapse = lastCollapsed.current ? w < 940 : w < 860;
          if (shouldCollapse !== lastCollapsed.current) {
            lastCollapsed.current = shouldCollapse;
            setToolbarCollapsed(shouldCollapse);
          }
        }
      });
    });
    observer.observe(el);
    return () => { observer.disconnect(); cancelAnimationFrame(rafId); };
  }, []);

  // --- Filtered data (client-side: toolbar filters + query builder) ---
  const filteredClientData = useMemo(() => {
    if (!clientData) return [];
    let data = [...clientData];

    // Toolbar filter chip values
    Object.entries(filterValues).forEach(([filterId, val]) => {
      if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) return;
      const filterDef = filters.find(f => f.id === filterId);
      if (!filterDef) return;
      const col = columns.find(c => c.id === filterId);
      if (!col) return;

      data = data.filter(row => {
        const cellVal = col.accessorFn ? col.accessorFn(row) : (col.accessorKey ? (row as any)[col.accessorKey] : '');
        const cellStr = cellVal == null ? '' : String(cellVal).toLowerCase();
        if (filterDef.type === 'select' || filterDef.type === 'status') return cellStr === String(val).toLowerCase();
        if (filterDef.type === 'multi-select') {
          const selected = Array.isArray(val) ? val : [val];
          return selected.some((v) => cellStr.includes(String(v).toLowerCase()));
        }
        if (filterDef.type === 'text') return cellStr.includes(String(val).toLowerCase());
        return true;
      });
    });

    // Query builder
    if (activeQuery.conditions.length > 0) {
      data = data.filter(row => evaluateQuery(row, activeQuery, columns));
    }

    return data;
  }, [clientData, filterValues, filters, columns, activeQuery]);
  const tableData = isServerMode ? serverData : filteredClientData;

  // --- Build TanStack columns ---
  const tanstackColumns = useMemo(() => {
    const cols: TanStackColumnDef<T, any>[] = [];

    // Checkbox column only shown when multi-select mode is active
    if (selectable && multiSelectMode) {
      cols.push({
        id: '_select',
        header: ({ table }) => (
          <input type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
            className="rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500" />
        ),
        cell: ({ row, table }) => (
          <input type="checkbox"
            checked={row.getIsSelected()}
            onChange={() => { /* selection driven by onClick */ }}
            onClick={(e) => {
              e.stopPropagation();
              const rowIndex = table.getRowModel().rows.findIndex(r => r.id === row.id);
              if (e.shiftKey && lastSelectedRowIndex.current >= 0) {
                const lo = Math.min(lastSelectedRowIndex.current, rowIndex);
                const hi = Math.max(lastSelectedRowIndex.current, rowIndex);
                setRowSelection(prev => {
                  const next = { ...prev };
                  table.getRowModel().rows.slice(lo, hi + 1).forEach(r => {
                    next[getRowId(r.original)] = true;
                  });
                  return next;
                });
              } else {
                row.toggleSelected();
                lastSelectedRowIndex.current = rowIndex;
              }
            }}
            className="rounded-sm border-gray-300 text-orange-600 focus:ring-orange-500 cursor-pointer" />
        ),
        size: 44,
        minSize: 44,
        maxSize: 44,
        enableSorting: false,
        enableColumnFilter: false,
        enableResizing: false,
        meta: { pinned: 'left', headerClassName: 'bg-linear-to-b from-blue-50 to-blue-100 border-r-2 border-blue-200', cellClassName: 'bg-linear-to-b from-blue-50/60 to-blue-100/60 border-r-2 border-blue-200' },
      });
    }

    columns.forEach(col => {
      cols.push({
        id: col.id,
        header: col.header,
        accessorFn: col.accessorFn || (col.accessorKey ? (row: T) => (row as any)[col.accessorKey!] : undefined),
        cell: col.cell || (({ getValue }) => {
          const val = getValue() as string | number | null | undefined;
          return <span className={`text-sm text-gray-900 ${col.cellClassName || ''}`}>{val != null ? String(val) : '—'}</span>;
        }),
        enableSorting: col.sortable !== false,
        enableColumnFilter: col.filterable !== false,
        filterFn: (row, columnId, filterValue) => {
          // Date filter: compare dates
          if (columnId.includes('Date') || columnId === 'createdAt' || columnId === 'updatedAt') {
            if (!filterValue) return true;
            const cellValue = row.getValue(columnId) as string;
            if (!cellValue) return false;
            // Extract date part (YYYY-MM-DD) from ISO string or date string
            const cellDate = cellValue.split('T')[0];
            return cellDate === filterValue;
          }
          // Default string filter
          const cellValue = row.getValue(columnId);
          if (cellValue == null) return false;
          return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase());
        },
        size: typeof col.width === 'number' ? col.width : undefined,
        minSize: col.minWidth,
        maxSize: col.maxWidth,
        meta: { pinned: col.pinned, headerClassName: col.headerClassName, cellClassName: col.cellClassName },
      });
    });

    if (rowActions.length > 0 || (showRowChevron ?? !!onRowClick)) {
      cols.push({
        id: '_actions',
        header: 'Actions',
        cell: ({ row }) => {
          const original = row.original;
          const visibleActions = visibleRowActions(rowActions, original, true);
          const rowId = getRowId(original);
          return (
            <div className="flex items-center justify-end gap-1">
              {visibleActions.length > 0 && (
                <div className="relative">
                  <button
                    type="button"
                    aria-label="Row actions"
                    aria-haspopup="menu"
                    aria-expanded={actionMenuRowId === rowId}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (actionMenuRowId === rowId) {
                        closeActionMenu(false);
                      } else {
                        closeAllMenus();
                        openActionMenu(
                          rowId,
                          event.currentTarget,
                          event.currentTarget,
                          getGridRowElement(rowId),
                          visibleActions.length,
                          original,
                          visibleActions,
                        );
                      }
                    }}
                    className="p-1 rounded-sm hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </div>
              )}
              {(showRowChevron ?? !!onRowClick) && (
                <ChevronRight className="h-4 w-4 text-gray-400" />
              )}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        enableResizing: false,
        size: 90,
        minSize: 90,
        maxSize: 90,
        meta: { pinned: 'right' as const, headerClassName: 'bg-linear-to-b from-gray-100 to-gray-200 border-l-2 border-gray-300 text-gray-600', cellClassName: 'bg-linear-to-b from-gray-50 to-gray-100 border-l-2 border-gray-300' },
      });
    }

    return cols;
  }, [columns, selectable, multiSelectMode, rowActions, showRowChevron, onRowClick, actionMenuRowId, getRowId, closeActionMenu, closeAllMenus, openActionMenu, getGridRowElement]);

  // --- Stable references for useReactTable (prevents infinite re-render loop) ---
  const effectiveColumnOrder = useMemo(() => {
    if (columnOrder.length === 0) return undefined;
    const suppliedIds = new Set(tanstackColumns.map((column) => column.id));
    const userOrder = columnOrder.filter(id => (
      id !== '_actions' && id !== '_select' && suppliedIds.has(id)
    ));
    const knownIds = new Set(userOrder);
    const missingCols = tanstackColumns
      .map(c => (typeof c.id === 'string' ? c.id : ''))
      .filter(id => id && id !== '_actions' && id !== '_select' && !knownIds.has(id));
    return [
      ...(selectable && multiSelectMode ? ['_select'] : []),
      ...userOrder,
      ...missingCols,
      '_actions',
    ];
  }, [columnOrder, tanstackColumns, selectable, multiSelectMode]);

  const stableGetRowId = useCallback((row: T) => getRowId(row), [getRowId]);

  const handlePaginationChange = useCallback((updater: any) => {
    const prev = { pageIndex, pageSize: isScrollMode ? ALL_ROWS_PAGE_SIZE : currentPageSize };
    const next = typeof updater === 'function' ? updater(prev) : updater;
    setPageIndex(next.pageIndex);
    if (!isScrollMode) setCurrentPageSize(next.pageSize);
  }, [pageIndex, isScrollMode, currentPageSize]);

  const stableGlobalFilterFn = useCallback((row: any, _columnId: string, filterValue: string) => {
    if (!filterValue) return true;
    const q = filterValue.toLowerCase();
    return columns.some(col => {
      if (col.filterable === false) return false;
      // Columns may opt into searching fields they neither display nor sort by
      // via searchValue; otherwise fall back to the accessor (displayed value).
      const val = col.searchValue
        ? col.searchValue(row.original)
        : col.accessorFn ? col.accessorFn(row.original) : (col.accessorKey ? (row.original as any)[col.accessorKey] : '');
      return val != null && String(val).toLowerCase().includes(q);
    });
  }, [columns]);

  // Memoize model getters so TanStack doesn't see new references each render
  const coreRowModel = useMemo(() => getCoreRowModel(), []);
  const sortedRowModel = useMemo(() => getSortedRowModel(), []);
  const filteredRowModel = useMemo(() => getFilteredRowModel(), []);
  const paginationRowModel = useMemo(() => getPaginationRowModel(), []);

  // --- TanStack Table instance ---
  const table = useReactTable({
    data: tableData,
    columns: tanstackColumns,
    state: {
      sorting: effectiveSorting,
      globalFilter,
      columnFilters,
      columnVisibility,
      columnOrder: effectiveColumnOrder,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize: isScrollMode ? ALL_ROWS_PAGE_SIZE : currentPageSize,
      },
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: coreRowModel,
    getSortedRowModel: sortedRowModel,
    getFilteredRowModel: filteredRowModel,
    getPaginationRowModel: paginationRowModel,
    getRowId: stableGetRowId,
    enableRowSelection: true,
    enableMultiSort: true,
    columnResizeMode: 'onChange',
    globalFilterFn: stableGlobalFilterFn,
    manualFiltering: isServerMode,
    manualPagination: isServerMode,
    manualSorting: isServerMode,
    pageCount: isServerMode ? Math.max(1, Math.ceil(serverTotal / (isScrollMode ? ALL_ROWS_PAGE_SIZE : currentPageSize))) : undefined,
  });

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      const ids = Object.keys(rowSelection).filter(k => rowSelection[k]);
      onSelectionChange(ids);
    }
  }, [rowSelection, onSelectionChange]);

  // Prune selections for rows that left the dataset entirely (sync removals,
  // scope changes). Reconciles against the full client data — not tableData —
  // so a column filter/search hiding a selected row does not lose it. Server
  // mode is excluded: serverData only holds the current page, so pruning
  // against it would drop off-page selections.
  useEffect(() => {
    if (isServerMode || !clientData) return;
    setRowSelection(prev => {
      const selectedIds = Object.keys(prev).filter(k => prev[k]);
      if (selectedIds.length === 0) return prev;
      const liveIds = new Set(clientData.map(getRowId));
      const survivors = selectedIds.filter(id => liveIds.has(id));
      if (survivors.length === selectedIds.length) return prev;
      return Object.fromEntries(survivors.map(id => [id, true]));
    });
  }, [clientData, isServerMode, getRowId]);

  // --- Derived ---
  const selectedCount = Object.keys(rowSelection).filter(k => rowSelection[k]).length;
  // One source of truth for "something is narrowing this grid": the chip list
  // covers search text, toolbar filter values, per-column filter-row values,
  // and the query builder — the same mechanisms the data filtering applies.
  const activeFilterChips = useMemo(() => buildActiveFilterChips({
    searchText: globalFilter,
    filterValues,
    filters,
    columnFilters,
    columns,
    activeQuery,
  }), [globalFilter, filterValues, filters, columnFilters, columns, activeQuery]);
  const querySummary = useMemo(
    () => (activeQuery.conditions.length > 0 ? summarizeQueryGroup(activeQuery, columns) : ''),
    [activeQuery, columns],
  );
  const appliedFilterCount = countAppliedGridFilters(filterValues, columnFilters);
  const queryConditionCount = countQueryConditions(activeQuery);
  const hasActiveFilters = activeFilterChips.length > 0;
  const visibleColumnCount = columns.filter(c => columnVisibility[c.id] !== false).length;
  const totalColumnCount = columns.length;
  const pageRows = table.getRowModel().rows;
  const totalRows = isServerMode ? serverTotal : table.getFilteredRowModel().rows.length;
  const showColumnFilterEmptyRow = showColumnFilters && columnFilters.length > 0 && totalRows === 0;
  const pageSize = isScrollMode ? ALL_ROWS_PAGE_SIZE : currentPageSize;
  const pageCount = isServerMode ? Math.max(1, Math.ceil(serverTotal / pageSize)) : table.getPageCount();

  // Notify parent of filtered count changes
  useEffect(() => {
    onFilteredCountChange?.(totalRows);
  }, [totalRows, onFilteredCountChange]);

  // Grouping: compute group keys for each row and insert group header markers
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  // Helper: resolve a group value using the column's accessorFn for proper display
  const resolveGroupValue = useCallback((row: any, colId: string): string => {
    const colDef = columns.find(c => c.id === colId);
    if (colDef?.accessorFn) {
      const result = colDef.accessorFn(row);
      return result != null ? String(result) : '(empty)';
    }
    const val = row[colId];
    if (val == null) return '(empty)';
    if (typeof val === 'object') return val.name || val.code || val.label || '(empty)';
    return String(val);
  }, [columns]);
  const groupedPageRows = useMemo(() => {
    if (groupByColumns.length === 0) return pageRows.map(row => ({ type: 'row' as const, row }));
    const result: Array<{ type: 'group'; groupKey: string; groupLabel: string; columnId: string; count: number } | { type: 'row'; row: typeof pageRows[0] }> = [];
    let lastGroupKey = '';
    for (const row of pageRows) {
      const groupValues = groupByColumns.map(colId => resolveGroupValue(row.original, colId));
      const groupKey = groupValues.join(' / ');
      if (groupKey !== lastGroupKey) {
        const label = groupByColumns.map((colId, i) => {
          const colDef = columns.find(c => c.id === colId);
          const colHeader = colDef && typeof colDef.header === 'string' ? colDef.header : colId;
          return `${colHeader}: ${groupValues[i]}`;
        }).join(' · ');
        const count = pageRows.filter(r => {
          return groupByColumns.every((colId, i) => {
            return resolveGroupValue(r.original, colId) === groupValues[i];
          });
        }).length;
        result.push({ type: 'group', groupKey, groupLabel: label, columnId: groupByColumns[0], count });
        lastGroupKey = groupKey;
      }
      if (!collapsedGroups.has(groupKey)) {
        result.push({ type: 'row', row });
      }
    }
    return result;
  }, [pageRows, groupByColumns, collapsedGroups, columns, resolveGroupValue]);

  const clearAllFilters = useCallback(() => {
    clearSearch();
    setFilterValues({});
    setColumnFilters([]);
    setActiveQuery(emptyGroup());
  }, [clearSearch]);

  // Remove one chip's filter mechanism. Search/query chips clear wholesale;
  // filter/column chips remove just their own entry so the rest stay applied.
  const removeFilterChip = useCallback((chip: ActiveFilterChip) => {
    if (chip.kind === 'search') {
      clearSearch();
    } else if (chip.kind === 'filter' && chip.targetId) {
      setFilterValues(prev => {
        const next = { ...prev };
        delete next[chip.targetId as string];
        return next;
      });
    } else if (chip.kind === 'column' && chip.targetId) {
      setColumnFilters(prev => prev.filter(f => f.id !== chip.targetId));
    } else if (chip.kind === 'query') {
      setActiveQuery(emptyGroup());
    }
  }, [clearSearch]);

  // Handle page size change including "All (scroll)" mode
  const handlePageSizeChange = useCallback((val: string) => {
    if (val === 'all') {
      setIsScrollMode(true);
      setPageIndex(0);
    } else {
      setIsScrollMode(false);
      const size = Number(val);
      setCurrentPageSize(size);
      setPageIndex(0);
    }
  }, []);

  const finishRowDrag = () => {
    const isInternalDrag = rowDragSourceIdRef.current !== null;
    rowDragSourceIdRef.current = null;
    setRowDropTarget(null);
    setOutsideDropFeedback(null);
    if (isInternalDrag) rowDragDrop?.onDragEnd?.();
  };

  // Keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const isGridWrapper = event.target === event.currentTarget;
    const isFocusedDataRow = event.target instanceof HTMLElement && event.target.matches('tr[data-grid-row-id]');
    const opensRowActions = event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey);
    if (opensRowActions && rowActions.length > 0 && (isGridWrapper || isFocusedDataRow)) {
      event.preventDefault();
      event.stopPropagation();
      const focusedRowId = isFocusedDataRow ? (event.target as HTMLElement).dataset.gridRowId : undefined;
      const focusedRow = focusedRowId
        ? pageRows.find((candidate) => getRowId(candidate.original) === focusedRowId)
        : focusedRowIndex >= 0 ? pageRows[focusedRowIndex] : undefined;
      if (!focusedRow) return;
      const original = focusedRow.original;
      const visibleActions = visibleRowActions(rowActions, original, true);
      if (visibleActions.length === 0) return;
      const rowId = getRowId(original);
      const rowElement = getGridRowElement(rowId);
      closeAllMenus();
      openActionMenu(rowId, rowElement ?? tableRef.current, null, rowElement ?? tableRef.current, visibleActions.length, original, visibleActions);
      return;
    }
    if (!isGridWrapper) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedRowIndex(prev => Math.min(prev + 1, pageRows.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedRowIndex(prev => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && focusedRowIndex >= 0 && pageRows[focusedRowIndex] && onRowClick) {
      event.preventDefault();
      onRowClick(pageRows[focusedRowIndex].original);
    } else if (event.key === 'Escape') {
      // G-049: Escape during an active row drag cancels it — clear the drag
      // source and drop feedback immediately so a late drop is ignored (no
      // mutation), without waiting for the browser's native dragend.
      if (rowDragSourceIdRef.current !== null) {
        event.preventDefault();
        finishRowDrag();
        return;
      }
      setRowSelection({});
      setMultiSelectMode(false);
      setFocusedRowIndex(-1);
    }
  }, [focusedRowIndex, pageRows, onRowClick, rowActions, getRowId, getGridRowElement, openActionMenu, closeAllMenus, finishRowDrag]);

  // ============================================================
  // Render
  // ============================================================

  return (
    <div ref={gridRootRef} data-grid-focus-container="" tabIndex={-1} className={`bg-white rounded-xl shadow-xs border border-gray-200 flex flex-col overflow-hidden ${containerClassName || ''}`}>

      {/* ===== TOOLBAR ===== */}
      <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-white" ref={toolbarRef} role="toolbar" aria-label={`${gridId} grid toolbar`}>
        <div className="flex items-center gap-3">
          {/* LEFT: Search + toolbar filter dropdowns */}
          <div className={`relative ${toolbarCollapsed ? 'flex-1 min-w-0' : 'w-64'}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
            {searchInput !== '' && (
              <button onClick={clearSearch}
                aria-label="Clear search"
                title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {!toolbarCollapsed && toolbarLeftExtra}

          <div className="flex-1" />

          {/* RIGHT: Full buttons when wide, hamburger when collapsed */}
          {!toolbarCollapsed ? (
            <>
              <button onClick={() => setShowColumnFilters(!showColumnFilters)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${(showColumnFilters || appliedFilterCount > 0) ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                <SlidersHorizontal className="h-4 w-4" /> Filters
                {appliedFilterCount > 0 && (
                  <span data-testid="grid-filters-badge"
                    title={`${appliedFilterCount} filter${appliedFilterCount === 1 ? '' : 's'} applied`}
                    className="ml-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                    {appliedFilterCount}
                  </span>
                )}
              </button>

              {isTier2 && (
                <button onClick={() => setShowQueryBuilder(true)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-colors ${activeQuery.conditions.length > 0 ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                  <Zap className="h-4 w-4" /> Query
                  {queryConditionCount > 0 && (
                    <span data-testid="grid-query-badge"
                      title={querySummary}
                      className="ml-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                      {queryConditionCount}
                    </span>
                  )}
                </button>
              )}

              <button onClick={() => setShowManageColumns(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">
                <Settings2 className="h-4 w-4" /> Column Settings
                <span className="ml-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                  {visibleColumnCount}
                </span>
              </button>

              {exportable && (
                <div className="relative">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowExportMenu(prev => !prev); }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">
                    <Download className="h-4 w-4" /> Export
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-xl z-200 py-1"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}>
                      {EXPORT_FORMATS.map(fmt => (
                        <button
                          key={fmt.id}
                          onClick={() => {
                            const allRows = table.getFilteredRowModel().rows.map(r => r.original);
                            const visibleColumns = columns.filter(c => columnVisibility[c.id] !== false);
                            runExport(fmt.id, allRows, visibleColumns, gridId);
                            setShowExportMenu(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                          <fmt.icon className="h-4 w-4" /> {fmt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {toolbarActions}
            </>
          ) : (
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setShowOverflowMenu(prev => !prev); }}
                className="inline-flex items-center justify-center p-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                <Menu className="h-4 w-4" />
              </button>
              {showOverflowMenu && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-200 py-1"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}>
                  {toolbarLeftExtra && (
                    <div className="px-3 py-2 border-b border-gray-100">
                      {toolbarLeftExtra}
                    </div>
                  )}
                  <button
                    onClick={() => { setShowColumnFilters(!showColumnFilters); setShowOverflowMenu(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 hover:bg-gray-50 transition-colors ${(showColumnFilters || appliedFilterCount > 0) ? 'text-orange-700 bg-orange-50' : 'text-gray-700'}`}>
                    <SlidersHorizontal className="h-4 w-4" /> Filters
                    {appliedFilterCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                        {appliedFilterCount}
                      </span>
                    )}
                  </button>
                  {isTier2 && (
                    <button
                      onClick={() => { setShowQueryBuilder(true); setShowOverflowMenu(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 hover:bg-gray-50 transition-colors ${activeQuery.conditions.length > 0 ? 'text-orange-700 bg-orange-50' : 'text-gray-700'}`}>
                      <Zap className="h-4 w-4" /> Query
                      {queryConditionCount > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                          {queryConditionCount}
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => { setShowManageColumns(true); setShowOverflowMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                    <Settings2 className="h-4 w-4" /> Column Settings
                    <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                      {visibleColumnCount}
                    </span>
                  </button>
                  {exportable && (
                    <>
                      {EXPORT_FORMATS.map(fmt => (
                        <button
                          key={fmt.id}
                          onClick={() => {
                            const allRows = table.getFilteredRowModel().rows.map(r => r.original);
                            const visibleColumns = columns.filter(c => columnVisibility[c.id] !== false);
                            runExport(fmt.id, allRows, visibleColumns, gridId);
                            setShowOverflowMenu(false);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                          <fmt.icon className="h-4 w-4" /> Export {fmt.label}
                        </button>
                      ))}
                    </>
                  )}
                  {toolbarActions && (
                    <div className="border-t border-gray-100 px-3 py-2" onClick={() => setShowOverflowMenu(false)}>
                      {toolbarActions}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== ACTIVE FILTER BAR ===== */}
      {/* Every mechanism narrowing the grid — search, toolbar filter values,
          per-column filter-row values, and the query builder — shows here as a
          removable chip, so a restored search/filter from a previous session is
          visible and clearable even when its input row is hidden. */}
      {activeFilterChips.length > 0 && (
        <div data-testid="grid-active-filters-bar" role="status" aria-label="Active filters"
          className="shrink-0 px-4 py-2 bg-orange-50/70 border-b border-orange-100 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-orange-700 shrink-0">Filtered by:</span>
          {activeFilterChips.map(chip => (
            <span key={chip.key} data-testid={`filter-chip-${chip.key}`}
              title={chip.kind === 'query' && querySummary ? querySummary : `${chip.label}: ${chip.value}`}
              className="inline-flex items-center gap-1 max-w-[260px] px-2 py-0.5 rounded-full bg-white border border-orange-200 text-orange-800 text-xs font-medium">
              <span className="text-orange-500 shrink-0">{chip.label}:</span>
              <span className="truncate">{chip.value}</span>
              <button onClick={() => removeFilterChip(chip)}
                aria-label={`Clear ${chip.label} filter`}
                className="shrink-0 hover:text-orange-600 transition-colors">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button onClick={clearAllFilters} data-testid="grid-clear-all-filters"
            className="text-xs text-orange-600 hover:text-orange-800 font-medium ml-1 shrink-0">
            Clear all
          </button>
        </div>
      )}

      {/* ===== BULK ACTION BAR ===== */}
      {selectable && selectedCount > 0 && (
        <div className="shrink-0 px-4 py-2.5 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
          <span className="text-sm font-medium text-orange-800">{selectedCount} selected</span>
          <div className="flex items-center gap-2">
            {/* Multi Select toggle */}
            {!multiSelectMode && (
              <button
                onClick={() => setMultiSelectMode(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <CheckSquare className="h-3.5 w-3.5" /> Multi Select
              </button>
            )}
            {bulkActions.map(action => {
              return (
                <button key={action.id}
                  disabled={action.disabled}
                  title={action.tooltip}
                  onClick={() => {
                    if (action.disabled) return;
                    const selectedIds = new Set(Object.keys(rowSelection).filter(k => rowSelection[k]));
                    // Client mode: resolve against full data so toolbar/query filters that
                    // hide selected rows still export/include them. Server mode uses the
                    // current page only.
                    const selectedRows = (isServerMode ? tableData : clientData ?? []).filter(row => selectedIds.has(getRowId(row)));
                    action.onClick(selectedRows);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''} ${action.variant === 'danger' ? 'text-red-700 border-red-300 bg-white hover:bg-red-50' : 'text-gray-700 border-gray-300 bg-white hover:bg-gray-50'}`}>
                  {action.icon && <action.icon className="h-3.5 w-3.5" />}
                  {action.label}
                </button>
              );
            })}
            <button onClick={() => { setRowSelection({}); setMultiSelectMode(false); lastSelectedRowIndex.current = -1; }}
              className="ml-2 p-1.5 text-gray-400 hover:text-gray-600 rounded-sm">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ===== GROUPING BAR ===== */}
      {groupByColumns.length > 0 && (
        <div className="shrink-0 px-4 py-2 bg-blue-50 border-b border-blue-200 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-blue-700">Grouped by:</span>
          {groupByColumns.map(colId => {
            const colDef = columns.find(c => c.id === colId);
            const label = colDef && typeof colDef.header === 'string' ? colDef.header : colId;
            return (
              <span key={colId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                {label}
                <button onClick={() => setGroupByColumns(prev => prev.filter(id => id !== colId))}
                  className="hover:text-blue-600 transition-colors">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          <button onClick={() => { setGroupByColumns([]); setCollapsedGroups(new Set()); }}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-1">
            Clear All
          </button>
        </div>
      )}

      {/* ===== TABLE ===== */}
      {isLoading ? (
        <DataGridLoadingState columnCount={columns.length} />
      ) : serverError && !hasLoadedServerData.current ? (
        <DataGridEmptyState
          icon={emptyIcon}
          title="Unable to load data"
          subtitle={serverError.message}
          hasFilters={false}
          onClearFilters={clearAllFilters}
        />
      ) : totalRows === 0 && !showColumnFilterEmptyRow ? (
        <DataGridEmptyState
          icon={emptyIcon}
          title={emptyTitle}
          subtitle={emptySubtitle}
          cta={emptyCTA}
          hasFilters={hasActiveFilters}
          onClearFilters={clearAllFilters}
        />
      ) : (
        <div className="flex-1 overflow-auto" ref={tableRef} data-grid-focus-container="" tabIndex={0} onKeyDown={handleKeyDown}>
          <table style={{ width: table.getCenterTotalSize(), minWidth: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead className={`bg-gray-50 border-b border-gray-200 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} role="row">
                  {headerGroup.headers.map(header => {
                    const meta = header.column.columnDef.meta as any;
                    const isPinnedRight = meta?.pinned === 'right';
                    const isPinnedLeft = meta?.pinned === 'left';
                    const canSort = header.column.getCanSort();
                    return (
                      <th key={header.id}
                        className={`relative text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase select-none ${canSort ? 'cursor-pointer hover:text-gray-700' : ''} ${isPinnedRight ? `sticky right-0 z-20 shadow-[-2px_0_4px_rgba(0,0,0,0.06)] ${meta?.headerClassName || 'bg-gray-50'}` : isPinnedLeft ? `sticky left-0 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.06)] ${meta?.headerClassName || 'bg-gray-50'}` : `${meta?.headerClassName || 'bg-gray-50'}`}`}
                        style={{
                          width: header.getSize(),
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize,
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                        onContextMenu={(e) => {
                          if (header.column.id === '_select' || header.column.id === '_actions') return;
                          e.preventDefault();
                          closeAllMenus();
                          const hdrEstHeight = 6 * MENU_ITEM_HEIGHT + MENU_HEADER_HEIGHT + MENU_PADDING_Y;
                          const { left, top } = clampPopupPosition(
                            { x: e.clientX, y: e.clientY },
                            { width: 208, estimatedHeight: hdrEstHeight },
                            'cursor',
                          );
                          setHeaderContextMenu({ x: left, y: top, columnId: header.column.id });
                        }}>
                        <div className="flex items-center gap-1">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && header.column.getIsSorted() === 'asc' && (
                            <span className="inline-flex items-center">
                              <ChevronUp className="h-3 w-3 text-orange-600" />
                              {sorting.length > 1 && (
                                <span className="text-[9px] font-bold text-orange-600 -ml-0.5">
                                  {sorting.findIndex(s => s.id === header.column.id) + 1}
                                </span>
                              )}
                            </span>
                          )}
                          {canSort && header.column.getIsSorted() === 'desc' && (
                            <span className="inline-flex items-center">
                              <ChevronDown className="h-3 w-3 text-orange-600" />
                              {sorting.length > 1 && (
                                <span className="text-[9px] font-bold text-orange-600 -ml-0.5">
                                  {sorting.findIndex(s => s.id === header.column.id) + 1}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            onClick={(e) => e.stopPropagation()}
                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none hover:bg-orange-400 transition-colors ${header.column.getIsResizing() ? 'bg-orange-500' : 'bg-transparent'}`}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}

              {/* Per-column filter row — toggled by Filters button */}
              {showColumnFilters && (
                <tr className="bg-white border-b border-gray-200">
                  {table.getHeaderGroups()[0]?.headers.map(header => {
                    const meta = header.column.columnDef.meta as any;
                    const isPinnedRight = meta?.pinned === 'right';
                    const isPinnedLeft = meta?.pinned === 'left';
                    const canFilter = header.column.getCanFilter();
                    const colId = header.column.id;

                    if (!canFilter || colId === '_select' || colId === '_actions') {
                      return <th key={header.id} className={`px-4 py-2 ${isPinnedRight ? 'sticky right-0 bg-white shadow-[-2px_0_4px_rgba(0,0,0,0.06)]' : isPinnedLeft ? 'sticky left-0 z-10 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.06)]' : ''}`} />;
                    }

                    // Check if this column has a filter definition with options (for select)
                    const filterDef = filters.find(f => f.id === colId);
                    const hasOptions = filterDef?.options && filterDef.options.length > 0;
                    
                    // Check if this is a date column (by column id or filterType)
                    const isDateColumn = colId.includes('Date') || colId === 'createdAt' || colId === 'updatedAt' || 
                                        filterDef?.filterType === 'date' || columnFilterTypes?.[colId] === 'date';

                    return (
                      <th key={header.id} className={`px-4 py-2 ${isPinnedRight ? 'sticky right-0 bg-white shadow-[-2px_0_4px_rgba(0,0,0,0.06)]' : isPinnedLeft ? 'sticky left-0 z-10 bg-white shadow-[2px_0_4px_rgba(0,0,0,0.06)]' : ''}`}>
                        {hasOptions ? (
                          <select
                            value={(header.column.getFilterValue() as string) ?? ''}
                            onChange={(e) => header.column.setFilterValue(e.target.value || undefined)}
                            className="w-full px-2 py-1 border border-gray-200 rounded-sm text-xs text-gray-600 focus:ring-1 focus:ring-orange-500 focus:border-orange-500">
                            <option value="">All</option>
                            {filterDef!.options!.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : isDateColumn ? (
                          <DateFilterInput
                            value={(header.column.getFilterValue() as string) ?? ''}
                            onApply={(value) => header.column.setFilterValue(value || undefined)} />
                        ) : (
                          <input type="text"
                            value={(header.column.getFilterValue() as string) ?? ''}
                            onChange={(e) => header.column.setFilterValue(e.target.value || undefined)}
                            placeholder={`Filter...`}
                            className="w-full px-2 py-1 border border-gray-200 rounded-sm text-xs focus:ring-1 focus:ring-orange-500 focus:border-orange-500" />
                        )}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>

            <tbody className="divide-y divide-gray-100">
              {showColumnFilterEmptyRow ? (
                <tr>
                  <td colSpan={table.getVisibleLeafColumns().length} className="px-4 py-8 text-center">
                    <p className="text-gray-500 font-medium">No results match your filters</p>
                    <button onClick={clearAllFilters}
                      className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors">
                      Clear filters
                    </button>
                  </td>
                </tr>
              ) : groupedPageRows.map((entry, entryIndex) => {
                if (entry.type === 'group') {
                  const isCollapsed = collapsedGroups.has(entry.groupKey);
                  return (
                    <tr key={`group-${entry.groupKey}`}
                      className="bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
                      onClick={() => setCollapsedGroups(prev => {
                        const next = new Set(prev);
                        if (next.has(entry.groupKey)) next.delete(entry.groupKey); else next.add(entry.groupKey);
                        return next;
                      })}>
                      <td colSpan={table.getVisibleLeafColumns().length}
                        className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className="h-3.5 w-3.5 text-gray-500 transition-transform duration-200"
                            style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}
                          />
                          <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                            {entry.groupLabel} ({entry.count})
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                }
                const row = entry.row;
                const rowId = getRowId(row.original);
                const isDraggableRow = rowDragDrop?.canDrag(row.original) ?? false;
                const currentDropState = rowDropTarget?.rowId === rowId ? rowDropTarget.state : null;
                const blockedDropReason = currentDropState && !currentDropState.allowed
                  ? currentDropState.reason ?? 'This drop target is not allowed.'
                  : undefined;
                const isExpanded = expandedRowRender && expandedRowId === rowId;
                const isActiveRow = activeRowId != null && activeRowId === rowId;
                const rowIndex = entryIndex;
                const visibleCells = row.getVisibleCells();
                const pinnedRightCells = visibleCells.filter(cell => {
                  const meta = cell.column.columnDef.meta as any;
                  return meta?.pinned === 'right';
                });
                const expandedContentColumnCount = visibleCells.length - pinnedRightCells.length;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      role="row"
                      data-grid-row-id={rowId}
                      tabIndex={-1}
                      draggable={rowDragDrop ? isDraggableRow : undefined}
                      title={blockedDropReason}
                      aria-description={blockedDropReason ? `Drop target blocked: ${blockedDropReason}` : undefined}
                      className={`transition-all duration-200 ${(onRowClick || onRowExpand) ? 'cursor-pointer' : ''} ${isActiveRow ? 'ring-2 ring-blue-400/70 ring-inset bg-blue-50/40 shadow-[0_2px_8px_rgba(59,130,246,0.18)] relative z-1' : row.getIsSelected() ? 'bg-orange-50' : isExpanded ? 'bg-orange-50/50' : focusedRowIndex === rowIndex ? 'bg-gray-50' : 'hover:bg-gray-50'} ${currentDropState ? currentDropState.allowed ? 'bg-emerald-50 ring-2 ring-inset ring-emerald-500' : 'bg-red-50 ring-2 ring-inset ring-red-500' : ''}`}
                      style={isActiveRow ? { transform: 'scale(1.003) translateY(-1px)' } : undefined}
                      onPointerDown={() => {
                        if (suppressNextRowClickAfterDragRef.current || suppressNextRowDoubleClickAfterDragRef.current) {
                          suppressNextRowClickAfterDragRef.current = false;
                          suppressNextRowDoubleClickAfterDragRef.current = false;
                        }
                      }}
                      onDragStart={(event) => {
                        if (!rowDragDrop || !isDraggableRow) {
                          event.preventDefault();
                          return;
                        }
                        rowDragSourceIdRef.current = rowId;
                        suppressNextRowClickAfterDragRef.current = true;
                        suppressNextRowDoubleClickAfterDragRef.current = true;
                        rowDragDrop.onDragStart(row.original);
                        if (event.dataTransfer) {
                          event.dataTransfer.setData('text/plain', rowId);
                          event.dataTransfer.effectAllowed = 'move';
                        }
                      }}
                      onDragOver={(event) => {
                        if (!rowDragDrop) return;
                        // Present the drop target only while a drag is actually
                        // active (internal row drag or host-controlled external
                        // drag) — after Escape cancellation the grid must stop
                        // advertising itself as a target.
                        const isInternalDrag = rowDragSourceIdRef.current !== null;
                        const isHostControlledExternalDrag = !isInternalDrag && (rowDragDrop.isExternalDragActive?.() ?? false);
                        if (!isInternalDrag && !isHostControlledExternalDrag) return;
                        const state = rowDragDrop.getDropState(row.original);
                        if (!state) {
                          setRowDropTarget((current) => current?.rowId === rowId ? null : current);
                          return;
                        }
                        event.preventDefault();
                        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
                        setRowDropTarget((current) => (
                          current?.rowId === rowId
                          && current.state.allowed === state.allowed
                          && current.state.reason === state.reason
                            ? current
                            : { rowId, state }
                        ));
                      }}
                      onDragLeave={(event) => {
                        const relatedTarget = event.relatedTarget;
                        if (!(relatedTarget instanceof Node) || !event.currentTarget.contains(relatedTarget)) {
                          setRowDropTarget((current) => current?.rowId === rowId ? null : current);
                        }
                      }}
                      onDrop={(event) => {
                        if (!rowDragDrop) return;
                        const state = rowDragDrop.getDropState(row.original);
                        if (state) event.preventDefault();
                        const isInternalDrag = rowDragSourceIdRef.current !== null;
                        const isHostControlledExternalDrag = !isInternalDrag && (rowDragDrop.isExternalDragActive?.() ?? false);
                        if (state && (isInternalDrag || isHostControlledExternalDrag)) rowDragDrop.onDrop(row.original);
                        finishRowDrag();
                      }}
                      onDragEnd={finishRowDrag}
                      onClick={(e) => {
                        if (suppressNextRowClickAfterDragRef.current) {
                          suppressNextRowClickAfterDragRef.current = false;
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        if (multiSelectMode) {
                          if (e.shiftKey && lastSelectedRowIndex.current >= 0) {
                            const lo = Math.min(lastSelectedRowIndex.current, rowIndex);
                            const hi = Math.max(lastSelectedRowIndex.current, rowIndex);
                            setRowSelection(prev => {
                              const next = { ...prev };
                              pageRows.slice(lo, hi + 1).forEach(r => {
                                next[getRowId(r.original)] = true;
                              });
                              return next;
                            });
                          } else {
                            row.toggleSelected();
                            lastSelectedRowIndex.current = rowIndex;
                          }
                          return;
                        }
                        const handleClick = () => {
                          if (onRowExpand) {
                            onRowExpand(expandedRowId === rowId ? null : rowId);
                          } else if (onRowClick) {
                            onRowClick(row.original);
                          }
                        };
                        if (selectable) {
                          clearTimeout(clickTimer.current);
                          clickTimer.current = setTimeout(handleClick, 250);
                        } else {
                          handleClick();
                        }
                      }}
                      onDoubleClick={(event) => {
                        if (suppressNextRowDoubleClickAfterDragRef.current) {
                          suppressNextRowDoubleClickAfterDragRef.current = false;
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        if (selectable && !onRowExpand) {
                          clearTimeout(clickTimer.current);
                          setMultiSelectMode(true);
                          setRowSelection(prev => ({ ...prev, [rowId]: true }));
                          lastSelectedRowIndex.current = rowIndex;
                        }
                      }}
                      onContextMenu={(e) => {
                        if (rowActions.length > 0) {
                          e.preventDefault();
                          const visActions = visibleRowActions(rowActions, row.original, true);
                          if (visActions.length === 0) return;
                          closeAllMenus();
                          openActionMenu(
                            rowId,
                            { x: e.clientX, y: e.clientY },
                            null,
                            e.currentTarget,
                            visActions.length,
                            row.original,
                            visActions,
                            'cursor',
                          );
                        }
                      }}
                      onFocus={(event) => {
                        if (event.target === event.currentTarget) setFocusedRowIndex(rowIndex);
                      }}>
                      {visibleCells.map(cell => {
                        const meta = cell.column.columnDef.meta as any;
                        const isPinnedRight = meta?.pinned === 'right';
                        const isPinnedLeft = meta?.pinned === 'left';
                        return (
                          <td key={cell.id}
                            className={`px-4 py-3 ${isPinnedRight ? `sticky right-0 z-10 shadow-[-2px_0_4px_rgba(0,0,0,0.06)] ${isActiveRow ? 'bg-blue-50/40' : meta?.cellClassName || 'bg-white'}` : isPinnedLeft ? `sticky left-0 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.06)] ${isActiveRow ? 'bg-blue-50/40' : meta?.cellClassName || 'bg-white'}` : `${meta?.cellClassName || ''}`}`}
                            style={{
                              width: cell.column.getSize(),
                              minWidth: cell.column.columnDef.minSize,
                              maxWidth: cell.column.columnDef.maxSize,
                              overflow: 'hidden',
                            }}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })}
                    </tr>
                    {isExpanded && (
                      <tr onDragEndCapture={() => setOutsideDropFeedback(null)}>
                        {expandedContentColumnCount > 0 && (
                          <td colSpan={expandedContentColumnCount} className="p-0 bg-gray-50/80 border-b border-gray-200">
                            <div className="w-full min-w-0">
                              {expandedRowRender(row.original, { openRowActions: openExpandedRowActions })}
                            </div>
                          </td>
                        )}
                        {pinnedRightCells.map(cell => {
                          const meta = cell.column.columnDef.meta as any;
                          return (
                            <td
                              key={`expanded-${cell.id}`}
                              aria-hidden="true"
                              className={`sticky right-0 z-10 border-b border-gray-200 shadow-[-2px_0_4px_rgba(0,0,0,0.06)] ${meta?.cellClassName || 'bg-white'}`}
                              style={{
                                width: cell.column.getSize(),
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize,
                              }}
                            />
                          );
                        })}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== PAGINATION FOOTER ===== */}
      {totalRows > 0 && (
        <div className="shrink-0 px-2 py-1.5 border-t border-gray-200 bg-white flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-500">
              Showing{' '}
              <span className="font-medium text-gray-700">
                {isScrollMode ? 1 : pageIndex * pageSize + 1}
              </span>–
              <span className="font-medium text-gray-700">
                {isScrollMode ? totalRows : Math.min((pageIndex + 1) * pageSize, totalRows)}
              </span>
              {' '}of <span className="font-medium text-gray-700">{totalRows}</span>
              {!isServerMode && hasActiveFilters && clientData && clientData.length !== totalRows && (
                <span className="text-xs text-orange-600 font-medium"> (filtered from {clientData.length})</span>
              )}
            </p>
            <select
              value={isScrollMode ? 'all' : String(pageSize)}
              onChange={(e) => handlePageSizeChange(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-sm text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
              {pageSizeOptions.map(s => (
                <option key={s} value={String(s)}>{s} / page</option>
              ))}
              <option value="all">All (scroll)</option>
            </select>
          </div>

          {!isScrollMode && (
            <div className="flex items-center gap-1">
              <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}
                className="p-1.5 rounded-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronsLeft className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}
                className="p-1.5 rounded-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="h-4 w-4 text-gray-600" />
              </button>

              {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
                let p: number;
                if (pageCount <= 7) p = i;
                else if (pageIndex <= 3) p = i;
                else if (pageIndex >= pageCount - 4) p = pageCount - 7 + i;
                else p = pageIndex - 3 + i;
                return (
                  <button key={p} onClick={() => table.setPageIndex(p)}
                    className={`min-w-[32px] h-8 px-2 rounded-sm text-sm font-medium transition-colors ${p === pageIndex ? 'bg-orange-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
                    {p + 1}
                  </button>
                );
              })}

              <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}
                className="p-1.5 rounded-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </button>
              <button onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}
                className="p-1.5 rounded-sm hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronsRight className="h-4 w-4 text-gray-600" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== QUERY BUILDER MODAL ===== */}
      {showQueryBuilder && (
        <QueryBuilderModal
          columns={columns}
          data={tableData}
          query={activeQuery}
          onApply={setActiveQuery}
          onClose={() => setShowQueryBuilder(false)}
        />
      )}

      {/* ===== COLUMN SETTINGS MODAL ===== */}
      {showManageColumns && (
        <ManageColumnsModal
          columns={columns}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          sorting={sorting}
          groupByColumns={groupByColumns}
          onApply={(vis, order, newSorting, newGroupBy, editedVisibilityIds, resetToFactory) => {
            const reconciled = reconcileColumnLayout(columns, vis, order);
            const retainedOrder = retainUnsuppliedColumnLayout(
              reconciled,
              resetToFactory ? {} : { order: columnOrder },
              suppliedColumnIdsRef.current!,
            ).order;
            const visibility = applyUserColumnVisibility(reconciled.visibility, editedVisibilityIds, resetToFactory);
            setColumnOrder(retainedOrder);
            setSorting(newSorting);
            setGroupByColumns(newGroupBy);
            try {
              localStorage.setItem(gridColumnVisibilityKey(gridId), JSON.stringify(visibility));
              localStorage.setItem(gridColumnOrderKey(gridId), JSON.stringify(retainedOrder));
            } catch { /* ignore quota / private mode */ }
            onStateChange?.({
              columnVisibility: visibility,
              columnOrder: retainedOrder,
              sorting: newSorting,
              filterValues,
              columnFilters,
              pageSize: isScrollMode ? 99999 : currentPageSize,
              searchText: globalFilter,
              activeQuery: activeQuery.conditions.length > 0 ? activeQuery : undefined,
              groupByColumns: newGroupBy,
            });
          }}
          onClose={() => setShowManageColumns(false)}
        />
      )}

      {/* ===== COLUMN HEADER CONTEXT MENU ===== */}
      {headerContextMenu && (() => {
        const col = table.getAllLeafColumns().find(c => c.id === headerContextMenu.columnId);
        if (!col) return null;
        const colDef = col.columnDef;
        const headerLabel = typeof colDef.header === 'string' ? colDef.header : headerContextMenu.columnId;
        const canSort = col.getCanSort();
        const currentSort = col.getIsSorted();
        const isGrouped = groupByColumns.includes(headerContextMenu.columnId);
        return (
          <div
            className="fixed z-200 w-52 bg-white border border-gray-200 rounded-lg shadow-xl py-1"
            style={getPopupStyle(headerContextMenu.x, headerContextMenu.y, { margin: MENU_VIEWPORT_MARGIN, zIndex: 200 })}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => clearTimeout(menuAutoCloseTimer.current)}
            onMouseLeave={() => { menuAutoCloseTimer.current = setTimeout(closeAllMenus, 5000); }}>
            <div className="px-3 py-1.5 border-b border-gray-100">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{headerLabel}</p>
            </div>
            {canSort && (
              <>
                <button
                  onClick={() => {
                    setSorting([{ id: headerContextMenu.columnId, desc: false }]);
                    setHeaderContextMenu(null);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 hover:bg-gray-50 transition-colors ${currentSort === 'asc' ? 'text-orange-700 bg-orange-50' : 'text-gray-700'}`}>
                  <ChevronUp className="h-4 w-4" /> Sort Ascending
                </button>
                <button
                  onClick={() => {
                    setSorting([{ id: headerContextMenu.columnId, desc: true }]);
                    setHeaderContextMenu(null);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 hover:bg-gray-50 transition-colors ${currentSort === 'desc' ? 'text-orange-700 bg-orange-50' : 'text-gray-700'}`}>
                  <ChevronDown className="h-4 w-4" /> Sort Descending
                </button>
                {currentSort && (
                  <button
                    onClick={() => {
                      setSorting(prev => prev.filter(s => s.id !== headerContextMenu.columnId));
                      setHeaderContextMenu(null);
                    }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
                    <X className="h-4 w-4" /> Clear Sort
                  </button>
                )}
              </>
            )}
            <div className="border-t border-gray-100 my-0.5" />
            <button
              onClick={() => {
                setGroupByColumns(prev =>
                  prev.includes(headerContextMenu.columnId)
                    ? prev.filter(id => id !== headerContextMenu.columnId)
                    : [...prev, headerContextMenu.columnId]
                );
                setHeaderContextMenu(null);
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 hover:bg-gray-50 transition-colors ${isGrouped ? 'text-orange-700 bg-orange-50' : 'text-gray-700'}`}>
              <GripVertical className="h-4 w-4" /> {isGrouped ? 'Ungroup' : 'Group By'}
            </button>
            <button
              onClick={() => {
                applyUserColumnVisibility(
                  { ...columnVisibility, [headerContextMenu.columnId]: false },
                  [headerContextMenu.columnId],
                );
                setHeaderContextMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
              <EyeOff className="h-4 w-4" /> Hide Column
            </button>
            <div className="border-t border-gray-100 my-0.5" />
            <button
              onClick={() => {
                setShowManageColumns(true);
                setHeaderContextMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 text-gray-700 hover:bg-gray-50 transition-colors">
              <Settings2 className="h-4 w-4" /> Column Settings
            </button>
          </div>
        );
      })()}

      {/* ===== ACTION MENU (three dots) ===== */}
      {actionMenuRowId && actionMenuPos && (() => {
        const payload = actionMenuPayloadRef.current;
        const menuRow = payload ? null : table.getRowModel().rows.find(r => getRowId(r.original) === actionMenuRowId);
        const original = payload?.row ?? menuRow?.original;
        if (!original) return null;
        const visibleActions = payload?.actions ?? visibleRowActions<T>(rowActions, original as T, true);
        if (visibleActions.length === 0) return null;
        return (
          <div
            ref={actionMenuRef}
            role="menu"
            data-row-action-menu-level="root"
            aria-label="Row actions"
            className="fixed z-200 w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1"
            style={getPopupStyle(actionMenuPos.left, actionMenuPos.top, { margin: MENU_VIEWPORT_MARGIN, zIndex: 200 })}
            onKeyDown={handleActionMenuKeyDown}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}>
            {visibleActions.map(action => (
              (() => {
                const children = visibleRowActions<T>(action.children ?? [], original as T);
                const hasChildren = children.length > 0;
                const isExpanded = actionSubmenu?.actionId === action.id;
                const disabled = action.disabled?.(original) ?? false;
                return (
                  <button
                    key={action.id}
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    data-action-id={action.id}
                    data-action-has-children={hasChildren ? 'true' : 'false'}
                    data-submenu-parent={hasChildren && isExpanded ? 'true' : undefined}
                    aria-haspopup={hasChildren ? 'menu' : undefined}
                    aria-expanded={hasChildren ? isExpanded : undefined}
                    disabled={disabled}
                    onMouseEnter={(event) => {
                      if (hasChildren && !disabled) {
                        openActionSubmenu(action.id, event.currentTarget, children, false);
                      } else if (actionSubmenu) {
                        closeActionSubmenu(false);
                      }
                    }}
                    onClick={(event) => {
                      if (hasChildren) {
                        if (disabled) return;
                        openActionSubmenu(action.id, event.currentTarget, children, true);
                        return;
                      }
                      action.onClick(original);
                      closeActionMenu(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed ${action.variant === 'danger' ? 'text-red-600' : 'text-gray-700'}`}>
                    {action.icon && <action.icon className="h-4 w-4" />}
                    <span className="min-w-0 flex-1">{action.label}</span>
                    {hasChildren && <ChevronRight className="h-4 w-4 shrink-0" aria-hidden="true" />}
                  </button>
                );
              })()
            ))}
            {actionSubmenu && (
              <div
                role="menu"
                data-row-action-menu-level="submenu"
                aria-label={`${visibleActions.find((action) => action.id === actionSubmenu.actionId)?.label ?? 'Row action'} options`}
                className="fixed z-201 w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1"
                style={getPopupStyle(actionSubmenu.left, actionSubmenu.top, { margin: MENU_VIEWPORT_MARGIN, zIndex: 201 })}
                onKeyDown={handleActionMenuKeyDown}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}>
                {actionSubmenu.actions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    data-action-id={action.id}
                    disabled={action.disabled?.(original) ?? false}
                    onClick={() => {
                      action.onClick(original);
                      closeActionMenu(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed ${action.variant === 'danger' ? 'text-red-600' : 'text-gray-700'}`}>
                    {action.icon && <action.icon className="h-4 w-4" />}
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {rowDragDrop?.outsideDropTarget?.isVisible() && (() => {
        const outsideDropTarget = rowDragDrop.outsideDropTarget;
        const outsideDropState = outsideDropFeedback?.rowId === 'outside' ? outsideDropFeedback.state : null;
        return (
          <div
            data-testid="grid-outside-drop-target"
            role="group"
            aria-label={outsideDropTarget.label}
            aria-description={outsideDropState?.reason ? `Drop target blocked: ${outsideDropState.reason}` : undefined}
            title={outsideDropState?.reason}
            className={`fixed bottom-4 left-1/2 z-120 flex min-h-[48px] w-96 max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center justify-center rounded-lg border-2 border-dashed px-3 text-sm font-medium shadow-lg ${outsideDropState ? outsideDropState.allowed ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-red-500 bg-red-50 text-red-800' : 'border-gray-300 bg-white text-gray-700'}`}
            onDragOver={(event) => {
              const state = outsideDropTarget.getDropState();
              if (!state) {
                setOutsideDropFeedback((current) => current?.rowId === 'outside' ? null : current);
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              if (event.dataTransfer) event.dataTransfer.dropEffect = state.allowed ? 'move' : 'none';
              setOutsideDropFeedback((current) => (
                current?.rowId === 'outside'
                && current.state.allowed === state.allowed
                && current.state.reason === state.reason
                  ? current
                  : { rowId: 'outside', state }
              ));
            }}
            onDragLeave={(event) => {
              const relatedTarget = event.relatedTarget;
              if (!(relatedTarget instanceof Node) || !event.currentTarget.contains(relatedTarget)) {
                setOutsideDropFeedback((current) => current?.rowId === 'outside' ? null : current);
              }
            }}
            onDrop={(event) => {
              event.stopPropagation();
              const state = outsideDropTarget.getDropState();
              if (state) event.preventDefault();
              const isInternalDrag = rowDragSourceIdRef.current !== null;
              const isHostControlledExternalDrag = !isInternalDrag && (rowDragDrop?.isExternalDragActive?.() ?? false);
              if (state && isHostControlledExternalDrag) {
                outsideDropTarget.onDrop();
                rowDragDrop?.onDragEnd?.();
              }
              setOutsideDropFeedback(null);
            }}
          >
            <span>{outsideDropTarget.label}</span>
            {outsideDropState?.reason && <span className="ml-2 text-xs">{outsideDropState.reason}</span>}
          </div>
        );
      })()}

      {/* ===== ROW CONTEXT MENU ===== */}
    </div>
  );
}
